import React, { useState } from 'react'
import { StudentShell } from '@/components/shared/StudentShell'
import { ShareResultModal } from '@/components/student/ShareResultModal'
import { ResumeAttemptModal } from '@/components/student/ResumeAttemptModal'
import { router, Link, usePage } from '@inertiajs/react'
import type { PageProps } from '@/types'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { KatexRenderer, parseQuestionDirectionAndPrompt } from '@/components/shared/KatexRenderer'
import { StructuredSolutionRenderer, StructuredSolutionData } from '@/components/shared/StructuredSolutionRenderer'
import { QuestionContextCard, QuestionContextData } from '@/components/shared/QuestionContextCard'
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem
} from '@/components/ui/dropdown-menu'
import {
  CheckCircle2, XCircle, MinusCircle, RotateCcw, ArrowLeft,
  Award, Target, BarChart2, BookOpen, History, User, Users, ExternalLink,
  TrendingUp, Check, Zap, ShieldCheck, Share2, ChevronRight, ChevronDown, ChevronUp,
  Filter, Search, ChevronLeft, SlidersHorizontal, Loader2, Clock, GraduationCap, Layers,
  ChevronsDown, ChevronsUp, Trophy, Timer, Medal, Maximize2, Calendar
} from 'lucide-react'
import { formatPercentile, formatGrade, formatTime, formatNumber } from '@/lib/format'

import {
  Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts'

interface QuestionOption {
  id: number
  label: string
  text: string | null
  option_image_url?: string | null
  is_correct: boolean
}

interface QuestionStatsData {
  avg_time_spent_seconds?: number | null
  times_attempted?: number
  times_correct?: number
  accuracy_percentage?: number | null
}

interface PyqInfo {
  exam_name?: string
  year?: number | string
  shift?: string
  paper?: string
  frequency?: number
  source?: string
}

interface QuestionResult {
  id: number
  order_index?: number
  question_number?: number
  question_text: string
  question_image_url?: string | null
  figure_image_url?: string | null
  render_format: string
  explanation_text: string | null
  explanation_image_url?: string | null
  structured_solution?: StructuredSolutionData | null
  difficulty: string
  question_type: string
  is_correct: boolean | null
  marks_awarded: number
  marks_max: number
  selected_option_ids?: number[]
  selected_option_id: number | null
  numerical_response: number | null
  numerical_answer: number | null
  section_id?: number | null
  section_name?: string | null
  time_spent_seconds?: number | null
  is_pyq?: boolean
  is_exemplar?: boolean
  pyq_info?: PyqInfo | null
  primary_subject?: string | null
  primary_chapter?: string | null
  primary_section?: string | null
  target_time_seconds?: number | null
  stats?: QuestionStatsData | null
  question_context?: QuestionContextData | null
  options: QuestionOption[]
}

interface QuestionsPagination {
  data: QuestionResult[]
  current_page: number
  per_page: number
  total_count: number
  total_pages: number
  total_test_questions: number
}

interface FilterState {
  section_id?: string
  status: string
  difficulty: string
  question_type: string
  search: string
}

interface SectionMeta {
  id: number
  name: string
  total_questions: number
  max_attempts?: number | null
  duration_minutes?: number | null
  subject_hint?: string | null
}

export interface SectionBreakdownItem {
  id: number
  name: string
  subject_hint?: string | null
  total_questions: number
  max_attempts?: number | null
  duration_minutes?: number | null
  correct_count: number
  incorrect_count: number
  unattempted_count: number
  score: number
  max_score: number
  accuracy: number
  positive_marks?: number
  negative_marks?: number
}

interface AttemptResult {
  id: number
  attempt_number: number
  status: string
  total_score: number
  max_score: number
  accuracy: number
  correct_count: number
  incorrect_count: number
  unattempted_count: number
  breakdown: Record<string, {
    total: number
    correct: number
    incorrect: number
    unattempted: number
    score: number
  }>
  rank_percentile: number | null
  test_rank?: number | null
  total_candidates?: number | null
  time_spent_seconds: number
  completed_at: string
  score_display?: {
    mode: 'raw_score' | 'percentage' | 'percentile' | 'nta_score'
    primary_value: number
    primary_label: string
  }
}

interface PastAttempt {
  id: number
  attempt_number: number
  total_score: number
  max_score: number
  accuracy: number
  status: string
  completed_at: string
}

interface Props {
  attempt: AttemptResult
  attempt_user?: { id: number; name: string; email: string }
  test: {
    id: number
    title: string
    exam_name?: string
    language?: string
    exam_id: number | null
    duration_minutes?: number
    sections?: SectionMeta[]
  }
  section_breakdown?: SectionBreakdownItem[]
  questions_pagination?: QuestionsPagination
  question_results?: QuestionResult[]
  filters?: FilterState
  past_attempts: PastAttempt[]
  is_staff: boolean
}

function resolveBackNavigation(defaultHref: string, defaultLabel: string) {
  if (typeof window === 'undefined') {
    return { href: defaultHref, label: defaultLabel }
  }
  const params = new URLSearchParams(window.location.search)
  const returnTo = params.get('return_to')
  if (returnTo && returnTo.startsWith('/')) {
    return { href: returnTo, label: 'Back to Previous Page' }
  }
  return { href: defaultHref, label: defaultLabel }
}



export default function ResultShow({
  attempt,
  attempt_user,
  test,
  section_breakdown = [],
  questions_pagination,
  question_results = [],
  filters,
  past_attempts,
  is_staff
}: Props) {
  const [shareOpen, setShareOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<'overview' | 'breakdown' | 'questions' | 'history'>('overview')

  const hasSections = (section_breakdown && section_breakdown.length > 0) || Boolean(test.sections && test.sections.length > 0)

  // Server-side filter state
  const [sectionFilter, setSectionFilter] = useState<string>(filters?.section_id || 'all')
  const [statusFilter, setStatusFilter] = useState<string>(filters?.status || 'all')
  const [difficultyFilter, setDifficultyFilter] = useState<string>(filters?.difficulty || 'all')
  const [typeFilter, setTypeFilter] = useState<string>(filters?.question_type || 'all')
  const [searchQuery, setSearchQuery] = useState<string>(filters?.search || '')
  const [loadingQuestions, setLoadingQuestions] = useState<boolean>(false)

  // Collapsible Questions state (collapsed by default)
  const [expandedQuestions, setExpandedQuestions] = useState<Record<string | number, boolean>>({})

  const toggleQuestion = (id: string | number) => {
    setExpandedQuestions(prev => ({
      ...prev,
      [id]: !prev[id]
    }))
  }

  // Active filter counter
  const activeFiltersCount = (sectionFilter !== 'all' ? 1 : 0) +
    (statusFilter !== 'all' ? 1 : 0) +
    (difficultyFilter !== 'all' ? 1 : 0) +
    (typeFilter !== 'all' ? 1 : 0) +
    (searchQuery.trim() !== '' ? 1 : 0)

  // Desktop pagination state
  const [pageSize, setPageSize] = useState<number>(questions_pagination?.per_page || 10)
  const currentPage = questions_pagination?.current_page || 1
  const totalPages = questions_pagination?.total_pages || 1
  const totalFilteredCount = questions_pagination?.total_count ?? (question_results.length || 0)
  const totalTestQuestions = questions_pagination?.total_test_questions ?? (attempt.correct_count + attempt.incorrect_count + attempt.unattempted_count)

  // Mobile Infinite Scroll state (page size 5 with buffer prefetch)
  const initialQuestions = questions_pagination?.data || question_results
  const [mobileQuestions, setMobileQuestions] = useState<QuestionResult[]>(initialQuestions)
  const [mobilePage, setMobilePage] = useState<number>(questions_pagination?.current_page || 1)
  const [mobileLoadingMore, setMobileLoadingMore] = useState<boolean>(false)
  const mobilePrefetchBuffer = React.useRef<{ page: number; data: QuestionResult[]; filtersKey: string } | null>(null)

  // Keep mobile questions in sync on first page / filter reload
  React.useEffect(() => {
    if (questions_pagination?.data) {
      if (questions_pagination.current_page === 1) {
        setMobileQuestions(questions_pagination.data)
        setMobilePage(1)
        mobilePrefetchBuffer.current = null
      }
    }
  }, [questions_pagination])

  // Current filter key to invalidate prefetch buffer on filter change
  const currentFiltersKey = `${sectionFilter}_${statusFilter}_${difficultyFilter}_${typeFilter}_${searchQuery}`

  // Server fetch trigger for Desktop / Filter changes
  const applyServerFilters = React.useCallback((
    newFilters: { section_id?: string; status?: string; difficulty?: string; question_type?: string; search?: string },
    targetPage = 1,
    targetPageSize = pageSize
  ) => {
    const nextSection = newFilters.section_id !== undefined ? newFilters.section_id : sectionFilter
    const nextStatus = newFilters.status !== undefined ? newFilters.status : statusFilter
    const nextDiff = newFilters.difficulty !== undefined ? newFilters.difficulty : difficultyFilter
    const nextType = newFilters.question_type !== undefined ? newFilters.question_type : typeFilter
    const nextSearch = newFilters.search !== undefined ? newFilters.search : searchQuery

    mobilePrefetchBuffer.current = null

    router.get(
      window.location.pathname,
      {
        page: targetPage,
        per_page: targetPageSize,
        section_id: nextSection,
        status: nextStatus,
        difficulty: nextDiff,
        question_type: nextType,
        search: nextSearch
      },
      {
        preserveState: true,
        preserveScroll: true,
        only: ['questions_pagination', 'filters', 'section_breakdown'],
        onBefore: () => setLoadingQuestions(true),
        onFinish: () => setLoadingQuestions(false)
      }
    )
  }, [sectionFilter, statusFilter, difficultyFilter, typeFilter, searchQuery, pageSize])

  // Debounced search query trigger: 1000ms buffer before calling server API
  const debounceTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  React.useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    // Only fire if the query has changed from what the server already has
    if (searchQuery !== (filters?.search || '')) {
      debounceTimerRef.current = setTimeout(() => {
        applyServerFilters({ search: searchQuery.trim() }, 1, pageSize)
      }, 1000)
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [searchQuery, filters?.search, applyServerFilters, pageSize])

  const handleSearchImmediate = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
      applyServerFilters({ search: searchQuery.trim() }, 1, pageSize)
    }
  }

  // Mobile background prefetch effect: only fires when filters are settled and server response confirms more pages exist
  React.useEffect(() => {
    const isMobileViewport = typeof window !== 'undefined' && window.innerWidth < 768
    if (!isMobileViewport || activeTab !== 'questions') return
    if (loadingQuestions) return // Block prefetch while current page is actively loading

    // If the user is currently typing a new search query (not yet committed to server), DO NOT prefetch
    const activeServerSearch = filters?.search || ''
    if (searchQuery.trim() !== activeServerSearch.trim()) return

    const serverTotalPages = questions_pagination?.total_pages || 1
    const nextPage = mobilePage + 1

    // Only prefetch if the completed server response confirms there is indeed a next page
    if (nextPage > serverTotalPages || serverTotalPages <= 1) return

    // If buffer already holds the next page for current filters, skip
    if (
      mobilePrefetchBuffer.current &&
      mobilePrefetchBuffer.current.page === nextPage &&
      mobilePrefetchBuffer.current.filtersKey === currentFiltersKey
    ) {
      return
    }

    const abortController = new AbortController()

    const prefetchNextPage = async () => {
      try {
        const queryParams = new URLSearchParams({
          page: nextPage.toString(),
          per_page: '5',
          section_id: sectionFilter,
          status: statusFilter,
          difficulty: difficultyFilter,
          question_type: typeFilter,
          search: activeServerSearch
        })

        const res = await fetch(`${window.location.pathname}?${queryParams.toString()}`, {
          headers: { Accept: 'application/json' },
          signal: abortController.signal
        })

        if (res.ok) {
          const json = await res.json()
          if (json.questions && json.questions.length > 0) {
            mobilePrefetchBuffer.current = {
              page: nextPage,
              data: json.questions,
              filtersKey: currentFiltersKey
            }
          }
        }
      } catch (err) {
        // Ignore aborted requests
      }
    }

    prefetchNextPage()

    return () => {
      abortController.abort()
    }
  }, [mobilePage, questions_pagination?.total_pages, loadingQuestions, statusFilter, difficultyFilter, typeFilter, filters?.search, searchQuery, currentFiltersKey, activeTab])

  // Mobile Load More Handler: uses prefetch buffer if available or fetches live
  const handleLoadMoreMobile = async () => {
    const nextPage = mobilePage + 1
    if (nextPage > totalPages || mobileLoadingMore) return

    // 1. Instant load from Prefetch Buffer if ready
    if (
      mobilePrefetchBuffer.current &&
      mobilePrefetchBuffer.current.page === nextPage &&
      mobilePrefetchBuffer.current.filtersKey === currentFiltersKey
    ) {
      const bufferedData = mobilePrefetchBuffer.current.data
      mobilePrefetchBuffer.current = null
      setMobileQuestions((prev) => [...prev, ...bufferedData])
      setMobilePage(nextPage)
      return
    }

    // 2. Fetch live if buffer not ready
    setMobileLoadingMore(true)
    try {
      const queryParams = new URLSearchParams({
        page: nextPage.toString(),
        per_page: '5',
        status: statusFilter,
        difficulty: difficultyFilter,
        question_type: typeFilter,
        search: searchQuery
      })

      const res = await fetch(`${window.location.pathname}?${queryParams.toString()}`, {
        headers: { Accept: 'application/json' }
      })

      if (res.ok) {
        const json = await res.json()
        if (json.questions && json.questions.length > 0) {
          setMobileQuestions((prev) => [...prev, ...json.questions])
          setMobilePage(nextPage)
        }
      }
    } catch (err) {
      console.error('Failed to load more questions:', err)
    } finally {
      setMobileLoadingMore(false)
    }
  }

  const breakdownData = Object.entries(attempt.breakdown || {}).map(([subject, data]) => ({
    subject,
    ...data,
  }))

  const defaultBackHref = is_staff && attempt_user
    ? `/admin/users/${attempt_user.id}`
    : test.exam_id
      ? `/student/exams/${test.exam_id}`
      : '/student/exams'

  const defaultBackLabel = is_staff && attempt_user ? 'Back to Student Profile' : 'Back to Exam'
  const { href: backHref, label: backLabel } = resolveBackNavigation(defaultBackHref, defaultBackLabel)
  const { ongoing_attempt } = usePage<PageProps>().props
  const [conflictModalOpen, setConflictModalOpen] = useState(false)

  const initiateReattempt = () => {
    router.post(`/student/tests/${test.id}/attempts`)
  }

  const handleReattempt = () => {
    if (ongoing_attempt) {
      setConflictModalOpen(true)
      return
    }
    initiateReattempt()
  }

  const totalQuestions = totalTestQuestions
  const correctPct = totalQuestions ? Math.round((attempt.correct_count / totalQuestions) * 100) : 0
  const incorrectPct = totalQuestions ? Math.round((attempt.incorrect_count / totalQuestions) * 100) : 0
  const unattemptedPct = totalQuestions ? Math.round((attempt.unattempted_count / totalQuestions) * 100) : 0

  const currentDesktopQuestions = questions_pagination?.data || question_results

  const areAllExpanded = currentDesktopQuestions.length > 0 && currentDesktopQuestions.every(q => Boolean(expandedQuestions[q.id]))

  const toggleAllQuestions = () => {
    if (areAllExpanded) {
      setExpandedQuestions({})
    } else {
      const next: Record<string | number, boolean> = {}
      currentDesktopQuestions.forEach(q => {
        next[q.id] = true
      })
      setExpandedQuestions(next)
    }
  }

  const clearAllFilters = () => {
    setSectionFilter('all')
    setStatusFilter('all')
    setDifficultyFilter('all')
    setTypeFilter('all')
    setSearchQuery('')
    applyServerFilters({
      section_id: 'all',
      status: 'all',
      difficulty: 'all',
      question_type: 'all',
      search: ''
    }, 1, pageSize)
  }

  const scorePercent = attempt.max_score > 0 ? (attempt.total_score / attempt.max_score) * 100 : 0
  const grade = formatGrade(scorePercent)
  const tokenColor = {
    secondary: 'text-secondary',
    primary: 'text-primary',
    tertiary: 'text-tertiary',
    destructive: 'text-destructive',
    muted: 'text-muted-foreground',
  }[grade.token] || 'text-foreground'
  const tokenIconBg = {
    secondary: 'bg-secondary text-secondary-fg',
    primary: 'bg-primary text-primary-fg',
    tertiary: 'bg-tertiary text-tertiary-fg',
    destructive: 'bg-destructive text-destructive-foreground',
    muted: 'bg-muted text-muted-foreground',
  }[grade.token] || 'bg-muted text-muted-foreground'

  const mobilePercentileValue = attempt.rank_percentile != null
    ? (attempt.rank_percentile % 1 === 0
        ? `${Math.floor(attempt.rank_percentile)}${(() => {
            const v = Math.floor(attempt.rank_percentile) % 100
            if (v >= 11 && v <= 13) return 'th'
            switch (v % 10) {
              case 1: return 'st'
              case 2: return 'nd'
              case 3: return 'rd'
              default: return 'th'
            }
          })()}`
        : `${formatNumber(attempt.rank_percentile)}%`)
    : '—'

  return (
    <StudentShell>
      <ShareResultModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        testTitle={test?.title || 'Mock Test'}
        examName={test?.exam_name || 'NEET UG 2026'}
        score={attempt.total_score}
        maxScore={attempt.max_score}
        accuracy={attempt.accuracy}
        rankPercentile={attempt.rank_percentile}
        attemptNumber={attempt.attempt_number}
        timeSpentSeconds={attempt.time_spent_seconds}
        studentName={attempt_user?.name || 'Student'}
      />

      <div className="flex flex-col gap-6">
        {/* ─── MOBILE VIEW ONLY (< sm) ─── */}
        <div className="block sm:hidden space-y-3.5">
          {/* Top Bar: Clean Back Button + Quick Actions */}
          <div className="flex items-center justify-between gap-2 pt-0.5">
            <Button
              variant="outline"
              size="sm"
              asChild
              className="h-8 w-8 p-0 rounded-xl border-border bg-card text-foreground hover:bg-muted/40 shrink-0 shadow-2xs flex items-center justify-center"
              aria-label="Back"
            >
              <Link href={backHref}>
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>

            <div className="flex items-center gap-1.5">
              {!is_staff && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShareOpen(true)}
                  className="h-8 w-8 p-0 rounded-xl border-border bg-card text-muted-foreground hover:text-foreground hover:bg-muted/40 shrink-0 shadow-2xs flex items-center justify-center"
                  title="Share Result"
                  aria-label="Share Result"
                >
                  <Share2 className="h-4 w-4" />
                </Button>
              )}
              {!is_staff && (
                <Button
                  size="sm"
                  onClick={handleReattempt}
                  className="h-8 px-3 rounded-xl bg-primary text-primary-fg hover:bg-primary/90 text-xs font-bold shadow-2xs shrink-0 flex items-center gap-1.5"
                >
                  <RotateCcw className="h-3.5 w-3.5" /> Reattempt
                </Button>
              )}
            </div>
          </div>

          {/* Test Title & Meta */}
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-secondary/15 text-secondary border border-secondary/25 px-2.5 py-0.5 rounded-full shadow-2xs">
                Attempt #{attempt.attempt_number}
              </span>
              {test.exam_name && !test.title?.toLowerCase().includes(test.exam_name.toLowerCase()) && (
                <span className="inline-flex items-center text-[10px] font-semibold bg-muted text-muted-foreground px-2 py-0.5 rounded-full border border-border">
                  {test.exam_name}
                </span>
              )}
            </div>
            <h1 className="text-lg font-black text-foreground tracking-tight leading-snug">
              {test.title}
            </h1>
            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[11px] text-muted-foreground pt-0.5">
              <span className="inline-flex items-center gap-1 font-medium">
                <Clock className="h-3 w-3 text-muted-foreground/70 shrink-0" />
                <span>{test.duration_minutes || 180}m</span>
              </span>
              <span className="text-border">•</span>
              <span className="inline-flex items-center gap-1 font-medium">
                <BookOpen className="h-3 w-3 text-muted-foreground/70 shrink-0" />
                <span>{totalTestQuestions} Qs</span>
              </span>
              {attempt.completed_at && (
                <>
                  <span className="text-border">•</span>
                  <span className="inline-flex items-center gap-1 font-medium">
                    <Calendar className="h-3 w-3 text-muted-foreground/70 shrink-0" />
                    <span>Completed {attempt.completed_at}</span>
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Compact Score + Grade Card */}
          <div className="p-3.5 rounded-2xl border border-primary/20 bg-primary/5 shadow-2xs flex items-center justify-between gap-3">
            <div>
              <span className="text-[10px] font-bold text-primary uppercase tracking-wider block">Score Achieved</span>
              <div className="flex items-baseline gap-1.5 mt-0.5">
                <span className="text-2xl font-black text-foreground tracking-tight">
                  {attempt.score_display?.primary_label || formatNumber(attempt.total_score)}
                </span>
                {(!attempt.score_display || attempt.score_display.mode === 'raw_score') && (
                  <span className="text-xs font-semibold text-muted-foreground">/ {formatNumber(attempt.max_score)} pts</span>
                )}
              </div>
              <span className="text-[10px] text-muted-foreground font-medium">
                {attempt.score_display && attempt.score_display.mode !== 'raw_score'
                  ? `Raw: ${formatNumber(attempt.total_score)} / ${formatNumber(attempt.max_score)} pts (${formatNumber(scorePercent)}%)`
                  : `${formatNumber(scorePercent)}% of total marks`}
              </span>
            </div>
            <div className="flex items-center gap-2.5 shrink-0">
              <div className="text-right">
                <span className="text-[9px] font-bold text-muted-foreground uppercase block">Grade</span>
                <span className={`text-2xl font-black ${tokenColor}`}>{grade.grade}</span>
              </div>
              <div className={`w-10 h-10 rounded-xl ${tokenIconBg} flex items-center justify-center shadow-2xs`}>
                <Trophy className="h-5 w-5" />
              </div>
            </div>
          </div>

          {/* 3-Metric Supporting Strip */}
          <div className="grid grid-cols-3 gap-2">
            <div className="flex flex-col items-center justify-center py-2.5 px-1 rounded-2xl border border-secondary/20 bg-secondary/5 font-bold text-center">
              <span className="text-base sm:text-lg font-extrabold text-secondary tracking-tight truncate max-w-full">
                {formatNumber(attempt.accuracy)}%
              </span>
              <span className="text-[9px] font-semibold text-secondary uppercase tracking-wider mt-0.5 whitespace-nowrap">
                Accuracy
              </span>
            </div>

            <div className="flex flex-col items-center justify-center py-2.5 px-1 rounded-2xl border border-tertiary/20 bg-tertiary/5 font-bold text-center">
              <span className="text-base sm:text-lg font-extrabold text-tertiary tracking-tight truncate max-w-full">
                {attempt.score_display?.mode === 'percentile' || attempt.score_display?.mode === 'nta_score'
                  ? mobilePercentileValue
                  : (attempt.test_rank ? `#${attempt.test_rank}` : '—')}
              </span>
              <span className="text-[9px] font-semibold text-tertiary uppercase tracking-wider mt-0.5 whitespace-nowrap">
                {attempt.score_display?.mode === 'percentile' || attempt.score_display?.mode === 'nta_score' ? 'Percentile' : 'Rank'}
              </span>
            </div>

            <div className="flex flex-col items-center justify-center py-2.5 px-1 rounded-2xl border border-border bg-muted/40 font-bold text-center">
              <span className="text-base sm:text-lg font-extrabold text-foreground tracking-tight truncate max-w-full">
                {formatTime(attempt.time_spent_seconds)}
              </span>
              <span className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider mt-0.5 whitespace-nowrap">
                Time
              </span>
            </div>
          </div>
        </div>

        {/* ─── DESKTOP VIEW ONLY (>= sm) ─── */}
        <div className="hidden sm:block space-y-6">
          {/* Navigation & Action Bar */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
          <Button
            variant="outline"
            size="sm"
            asChild
            className="rounded-xl border-border/80 bg-card/60 backdrop-blur-glass text-foreground hover:bg-primary/10 hover:border-primary/50 transition-all shadow-glass-xs h-9 px-3.5"
          >
            <Link href={backHref}>
              <ArrowLeft className="mr-2 h-4 w-4 text-muted-foreground" />
              <span>{backLabel}</span>
            </Link>
          </Button>

          <div className="flex items-center gap-2">
            {!is_staff && (
              <Button variant="outline" size="sm" onClick={() => setShareOpen(true)} className="flex-1 sm:flex-initial gap-2 rounded-xl border-secondary/40 text-secondary hover:bg-secondary/10">
                <Share2 className="h-4 w-4 text-secondary" /> Share Result
              </Button>
            )}
            {!is_staff && (
              <Button variant="outline" size="sm" onClick={handleReattempt} className="flex-1 sm:flex-initial gap-2 font-bold rounded-xl border-primary/40 text-primary hover:bg-primary/10">
                <RotateCcw className="h-4 w-4" /> Reattempt
              </Button>
            )}
          </div>
        </div>

        {/* Staff Context Inspection Banner */}
        {is_staff && attempt_user && (
          <div className="rounded-2xl border border-primary/30 bg-primary/10 p-4 flex flex-wrap items-center justify-between gap-3 shadow-glass-sm">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-primary text-primary-fg flex items-center justify-center font-bold shadow-xs">
                <User className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs font-bold text-primary uppercase tracking-wider">Staff Inspection Mode</div>
                <div className="text-sm font-semibold text-foreground">
                  Viewing performance report for <strong className="text-primary">{attempt_user.name}</strong> ({attempt_user.email})
                </div>
              </div>
            </div>
            <Button size="sm" variant="outline" className="h-9 text-xs gap-1.5 rounded-xl border-primary/30 hover:bg-primary/15" asChild>
              <Link href={`/admin/users/${attempt_user.id}`}>
                View Student Profile <ExternalLink className="h-3.5 w-3.5" />
              </Link>
            </Button>
          </div>
        )}

        {/* Header Hero Card Banner */}
        <div className="rounded-2xl border border-border/70 bg-card p-4 sm:p-6 lg:p-8 shadow-xs">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5">
            <div className="space-y-2 max-w-2xl">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] font-bold uppercase tracking-wider text-primary bg-primary/15 px-2.5 py-0.5 rounded-full border border-primary/25 inline-flex items-center gap-1.5 shadow-2xs">
                  <Award className="h-3 w-3 text-primary" />
                  <span>Evaluation Report</span>
                </span>
                <Badge variant="outline" className="text-[10px] font-bold bg-secondary/15 text-secondary border-secondary/30 rounded-full px-2.5 py-0.5 shadow-2xs">
                  Attempt #{attempt.attempt_number}
                </Badge>
                <Badge variant="outline" className="text-[10px] font-bold bg-primary/15 text-primary border-primary/30 rounded-full px-2.5 py-0.5 flex items-center gap-1 shadow-2xs">
                  <CheckCircle2 className="h-3 w-3" /> Evaluated
                </Badge>
              </div>

              <h1 className="text-lg sm:text-2xl font-bold text-foreground tracking-tight">
                {test.title}
              </h1>

              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                Completed on {attempt.completed_at} • Duration limit: {test.duration_minutes || 180} Mins • {totalTestQuestions} Questions
              </p>
            </div>

            {/* Quick Score Spotlight Chip */}
            <div className="flex items-center justify-between sm:justify-start gap-4 shrink-0 bg-primary/10 p-3.5 sm:p-4 rounded-2xl border border-primary/20 backdrop-blur-xs shadow-glass-xs">
              <div>
                <p className="text-[10px] sm:text-[11px] font-bold text-primary uppercase tracking-wider">Score Achieved</p>
                <p className="text-2xl sm:text-3xl font-extrabold text-foreground tracking-tight mt-0.5">
                  {attempt.score_display?.primary_label || `${formatNumber(attempt.total_score)} / ${formatNumber(attempt.max_score)}`}
                </p>
                {attempt.score_display && attempt.score_display.mode !== 'raw_score' && (
                  <p className="text-[10px] text-muted-foreground font-medium">
                    Raw: {formatNumber(attempt.total_score)} / {formatNumber(attempt.max_score)} pts
                  </p>
                )}
              </div>
              <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-primary text-primary-fg flex items-center justify-center font-extrabold shadow-glass-sm shrink-0">
                <Award className="h-5 w-5 sm:h-6 sm:w-6" />
              </div>
            </div>
          </div>
        </div>

        {/* KPI Scorecard — Score+Grade Hero + 3 Supporting Metrics */}
        {(() => {
          const scorePercent = attempt.max_score > 0 ? (attempt.total_score / attempt.max_score) * 100 : 0
          const grade = formatGrade(scorePercent)
          const tokenColor = {
            secondary: 'text-secondary',
            primary: 'text-primary',
            tertiary: 'text-tertiary',
            destructive: 'text-destructive',
            muted: 'text-muted-foreground',
          }[grade.token] || 'text-foreground'
          const tokenBg = {
            secondary: 'bg-secondary/10 border-secondary/25',
            primary: 'bg-primary/10 border-primary/25',
            tertiary: 'bg-tertiary/10 border-tertiary/25',
            destructive: 'bg-destructive/10 border-destructive/25',
            muted: 'bg-muted border-border',
          }[grade.token] || 'bg-muted border-border'
          const tokenIconBg = {
            secondary: 'bg-secondary text-secondary-fg',
            primary: 'bg-primary text-primary-fg',
            tertiary: 'bg-tertiary text-tertiary-fg',
            destructive: 'bg-destructive text-destructive-foreground',
            muted: 'bg-muted text-muted-foreground',
          }[grade.token] || 'bg-muted text-muted-foreground'

          return (
            <div className="space-y-3">
              {/* Primary Score + Grade Hero Card */}
              <Card className="border-border/80 bg-card/60 backdrop-blur-glass shadow-glass-sm rounded-2xl overflow-hidden">
                <CardContent className="p-0">
                  <div className="flex flex-col sm:flex-row items-stretch">
                    {/* Score section */}
                    <div className="flex-1 p-5 sm:p-6 space-y-2 border-b sm:border-b-0 sm:border-r border-border/40">
                      <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">Total Score</p>
                      <div className="flex items-baseline gap-2">
                        <span className="text-4xl sm:text-5xl font-extrabold text-foreground tracking-tight">
                          {attempt.score_display?.primary_label || formatNumber(attempt.total_score)}
                        </span>
                        {(!attempt.score_display || attempt.score_display.mode === 'raw_score') && (
                          <span className="text-base sm:text-lg font-semibold text-muted-foreground">
                            / {formatNumber(attempt.max_score)} pts
                          </span>
                        )}
                      </div>
                      {/* Score bar */}
                      <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden mt-2">
                        <div
                          className={`h-full rounded-full transition-all duration-700 ${
                            grade.token === 'secondary' ? 'bg-secondary'
                              : grade.token === 'primary' ? 'bg-primary'
                              : grade.token === 'tertiary' ? 'bg-tertiary'
                              : grade.token === 'destructive' ? 'bg-destructive'
                              : 'bg-muted-foreground'
                          }`}
                          style={{ width: `${Math.min(100, Math.max(0, scorePercent))}%` }}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground font-medium">
                        {attempt.score_display && attempt.score_display.mode !== 'raw_score'
                          ? `Raw Score: ${formatNumber(attempt.total_score)} / ${formatNumber(attempt.max_score)} pts (${formatNumber(scorePercent)}% of total marks)`
                          : `${formatNumber(scorePercent)}% of total marks`}
                      </p>
                    </div>

                    {/* Grade section */}
                    <div className={`flex flex-row sm:flex-col items-center justify-between sm:justify-center gap-3 sm:gap-1.5 px-5 py-4 sm:py-6 sm:px-8 ${tokenBg} border sm:border-0 sm:border-none`}>
                      <div className="text-center">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Grade</p>
                        <span className={`text-4xl sm:text-5xl font-black tracking-tighter ${tokenColor}`}>
                          {grade.grade}
                        </span>
                      </div>
                      <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl ${tokenIconBg} flex items-center justify-center shadow-glass-sm`}>
                        <Trophy className="h-5 w-5 sm:h-6 sm:w-6" />
                      </div>
                      <p className={`text-[11px] font-semibold ${tokenColor} sm:text-center`}>{grade.label}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* 3 Supporting Metric Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {/* Accuracy */}
                <Card className="p-4 sm:p-5 flex items-center justify-between shadow-glass-sm border-border/80 bg-card/60 backdrop-blur-glass rounded-2xl">
                  <div className="space-y-1 min-w-0">
                    <p className="text-[10px] sm:text-[11px] font-bold text-muted-foreground uppercase tracking-wider">Accuracy</p>
                    <p className="text-xl sm:text-2xl font-extrabold text-secondary tracking-tight">{formatNumber(attempt.accuracy)}%</p>
                    <p className="text-[10px] sm:text-[11px] text-muted-foreground font-medium">{attempt.correct_count} of {totalTestQuestions} correct</p>
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-secondary/10 text-secondary flex items-center justify-center shadow-xs shrink-0 border border-secondary/20">
                    <Target className="h-5 w-5" />
                  </div>
                </Card>

                {/* Rank / Percentile */}
                <Card className="p-4 sm:p-5 flex items-center justify-between shadow-glass-sm border-border/80 bg-card/60 backdrop-blur-glass rounded-2xl">
                  <div className="space-y-1 min-w-0">
                    <p className="text-[10px] sm:text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
                      {attempt.score_display?.mode === 'percentile' || attempt.score_display?.mode === 'nta_score' ? 'Percentile' : 'Nationwide Rank'}
                    </p>
                    <p className="text-xl sm:text-2xl font-extrabold text-tertiary tracking-tight">
                      {attempt.score_display?.mode === 'percentile' || attempt.score_display?.mode === 'nta_score'
                        ? formatPercentile(attempt.rank_percentile)
                        : (attempt.test_rank ? `#${attempt.test_rank}` : '—')}
                    </p>
                    <p className="text-[10px] sm:text-[11px] text-muted-foreground font-medium truncate">
                      {attempt.score_display?.mode === 'percentile' || attempt.score_display?.mode === 'nta_score'
                        ? (attempt.test_rank && attempt.total_candidates
                          ? `AIR #${attempt.test_rank} / ${attempt.total_candidates.toLocaleString()}`
                          : 'Nationwide ranking')
                        : (attempt.total_candidates
                          ? `of ${attempt.total_candidates.toLocaleString()} ${attempt.total_candidates === 1 ? 'candidate' : 'candidates'}`
                          : 'Nationwide ranking')}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-tertiary/10 text-tertiary flex items-center justify-center shadow-xs shrink-0 border border-tertiary/20">
                    <Medal className="h-5 w-5" />
                  </div>
                </Card>

                {/* Time Spent */}
                <Card className="p-4 sm:p-5 flex items-center justify-between shadow-glass-sm border-border/80 bg-card/60 backdrop-blur-glass rounded-2xl">
                  <div className="space-y-1 min-w-0">
                    <p className="text-[10px] sm:text-[11px] font-bold text-muted-foreground uppercase tracking-wider">Time Spent</p>
                    <p className="text-xl sm:text-2xl font-extrabold text-foreground tracking-tight">{formatTime(attempt.time_spent_seconds)}</p>
                    <p className="text-[10px] sm:text-[11px] text-muted-foreground font-medium">Limit: {test.duration_minutes || 180} mins</p>
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-muted/60 flex items-center justify-center shadow-xs shrink-0 border border-border/60">
                    <Timer className="h-5 w-5 text-muted-foreground" />
                  </div>
                </Card>
              </div>
            </div>
          )
        })()}
        </div>

        {/* Mobile View: Responsive Segmented Pill Tabs */}
        {past_attempts.length <= 1 ? (
          <div className="grid grid-cols-3 sm:hidden p-1 rounded-2xl bg-muted/40 border border-border/60 gap-1">
            <button
              onClick={() => setActiveTab('overview')}
              className={`flex items-center justify-center gap-1.5 py-2 px-1 rounded-xl text-xs transition-all cursor-pointer ${
                activeTab === 'overview'
                  ? 'bg-primary text-primary-fg font-extrabold shadow-2xs'
                  : 'text-muted-foreground hover:text-foreground font-semibold hover:bg-card/50'
              }`}
            >
              <Zap className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">Overview</span>
            </button>
            <button
              onClick={() => setActiveTab('breakdown')}
              className={`flex items-center justify-center gap-1.5 py-2 px-1 rounded-xl text-xs transition-all cursor-pointer ${
                activeTab === 'breakdown'
                  ? 'bg-primary text-primary-fg font-extrabold shadow-2xs'
                  : 'text-muted-foreground hover:text-foreground font-semibold hover:bg-card/50'
              }`}
            >
              {hasSections ? <Layers className="h-3.5 w-3.5 shrink-0" /> : <BarChart2 className="h-3.5 w-3.5 shrink-0" />}
              <span className="truncate">{hasSections ? 'Sections' : 'Subjects'}</span>
            </button>
            <button
              onClick={() => setActiveTab('questions')}
              className={`flex items-center justify-center gap-1.5 py-2 px-1 rounded-xl text-xs transition-all cursor-pointer ${
                activeTab === 'questions'
                  ? 'bg-primary text-primary-fg font-extrabold shadow-2xs'
                  : 'text-muted-foreground hover:text-foreground font-semibold hover:bg-card/50'
              }`}
            >
              <BookOpen className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">Solutions</span>
              <span className="hidden min-[380px]:inline text-[10px] opacity-75 font-mono">({totalQuestions})</span>
            </button>
          </div>
        ) : (
          <div className="flex sm:hidden items-center gap-1.5 p-1 rounded-2xl bg-muted/40 border border-border/60 overflow-x-auto touch-pan-x [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            <button
              onClick={() => setActiveTab('overview')}
              className={`flex-none px-2.5 py-1.5 rounded-xl text-xs transition-all whitespace-nowrap flex items-center gap-1.5 cursor-pointer ${
                activeTab === 'overview'
                  ? 'bg-primary text-primary-fg font-extrabold shadow-2xs'
                  : 'text-muted-foreground hover:text-foreground font-semibold hover:bg-card/50'
              }`}
            >
              <Zap className="h-3.5 w-3.5 shrink-0" /> Overview
            </button>
            <button
              onClick={() => setActiveTab('breakdown')}
              className={`flex-none px-2.5 py-1.5 rounded-xl text-xs transition-all whitespace-nowrap flex items-center gap-1.5 cursor-pointer ${
                activeTab === 'breakdown'
                  ? 'bg-primary text-primary-fg font-extrabold shadow-2xs'
                  : 'text-muted-foreground hover:text-foreground font-semibold hover:bg-card/50'
              }`}
            >
              {hasSections ? <Layers className="h-3.5 w-3.5 shrink-0" /> : <BarChart2 className="h-3.5 w-3.5 shrink-0" />}
              {hasSections ? 'Sections' : 'Subjects'}
            </button>
            <button
              onClick={() => setActiveTab('questions')}
              className={`flex-none px-2.5 py-1.5 rounded-xl text-xs transition-all whitespace-nowrap flex items-center gap-1.5 cursor-pointer ${
                activeTab === 'questions'
                  ? 'bg-primary text-primary-fg font-extrabold shadow-2xs'
                  : 'text-muted-foreground hover:text-foreground font-semibold hover:bg-card/50'
              }`}
            >
              <BookOpen className="h-3.5 w-3.5 shrink-0" /> Solutions
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`flex-none px-2.5 py-1.5 rounded-xl text-xs transition-all whitespace-nowrap flex items-center gap-1.5 cursor-pointer ${
                activeTab === 'history'
                  ? 'bg-primary text-primary-fg font-extrabold shadow-2xs'
                  : 'text-muted-foreground hover:text-foreground font-semibold hover:bg-card/50'
              }`}
            >
              <History className="h-3.5 w-3.5 shrink-0" /> Attempts ({past_attempts.length})
            </button>
          </div>
        )}


        {/* Desktop Navigation Pill Tabs */}
        <div className="hidden sm:flex items-center p-1 rounded-2xl bg-muted/50 border border-border/60 backdrop-blur-glass shadow-2xs gap-1.5">
          <button
            onClick={() => setActiveTab('overview')}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-xs sm:text-sm font-bold transition-all cursor-pointer ${
              activeTab === 'overview'
                ? 'bg-card text-foreground shadow-xs border border-border/40 font-extrabold'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'
            }`}
          >
            <Zap className="h-4 w-4 text-primary" /> Performance Overview
          </button>

          <button
            onClick={() => setActiveTab('breakdown')}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-xs sm:text-sm font-bold transition-all cursor-pointer ${
              activeTab === 'breakdown'
                ? 'bg-card text-foreground shadow-xs border border-border/40 font-extrabold'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'
            }`}
          >
            {hasSections ? (
              <><Layers className="h-4 w-4 text-secondary" /> Section Breakdown</>
            ) : (
              <><BarChart2 className="h-4 w-4 text-secondary" /> Subject Breakdown</>
            )}
          </button>

          <button
            onClick={() => setActiveTab('questions')}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-xs sm:text-sm font-bold transition-all cursor-pointer ${
              activeTab === 'questions'
                ? 'bg-card text-foreground shadow-xs border border-border/40 font-extrabold'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'
            }`}
          >
            <BookOpen className="h-4 w-4 text-tertiary" /> Question Solutions ({totalQuestions})
          </button>

          {past_attempts.length > 1 && (
            <button
              onClick={() => setActiveTab('history')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-xs sm:text-sm font-bold transition-all cursor-pointer ${
                activeTab === 'history'
                  ? 'bg-card text-foreground shadow-xs border border-border/40 font-extrabold'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'
              }`}
            >
              <History className="h-4 w-4 text-primary" /> Past Attempts ({past_attempts.length})
            </button>
          )}
        </div>

        {/* TAB 1: OVERVIEW */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 animate-fade-in">
            {/* Response Breakdown Cards */}
            <Card className="md:col-span-2 border-border/80 bg-card/60 backdrop-blur-glass shadow-glass-sm rounded-2xl">
              <CardHeader className="p-4 sm:p-6 pb-3 sm:pb-3 border-b border-border/40">
                <CardTitle className="text-sm sm:text-base font-extrabold flex items-center gap-2 text-foreground">
                  <BarChart2 className="h-4 w-4 sm:h-4.5 sm:w-4.5 text-primary" /> Question Response Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 sm:p-6 space-y-4 sm:space-y-6">
                {/* Mobile View: 3 Clean Spacious Horizontal Cards */}
                <div className="sm:hidden space-y-2">
                  {/* Correct Row */}
                  <div className="p-3 rounded-2xl bg-secondary/8 border border-secondary/25 flex items-center justify-between gap-3 shadow-2xs">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="w-8 h-8 rounded-xl bg-secondary/15 text-secondary flex items-center justify-center shrink-0 border border-secondary/20">
                        <CheckCircle2 className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs font-bold text-secondary">Correct</div>
                        <div className="text-[10px] text-muted-foreground font-medium">{correctPct}% of questions</div>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-base font-black text-foreground">
                        {attempt.correct_count} <span className="text-xs font-semibold text-muted-foreground">/ {totalQuestions}</span>
                      </div>
                    </div>
                  </div>

                  {/* Incorrect Row */}
                  <div className="p-3 rounded-2xl bg-destructive/8 border border-destructive/25 flex items-center justify-between gap-3 shadow-2xs">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="w-8 h-8 rounded-xl bg-destructive/15 text-destructive flex items-center justify-center shrink-0 border border-destructive/20">
                        <XCircle className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs font-bold text-destructive">Incorrect</div>
                        <div className="text-[10px] text-muted-foreground font-medium">{incorrectPct}% of questions</div>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-base font-black text-foreground">
                        {attempt.incorrect_count} <span className="text-xs font-semibold text-muted-foreground">/ {totalQuestions}</span>
                      </div>
                    </div>
                  </div>

                  {/* Unanswered Row */}
                  <div className="p-3 rounded-2xl bg-muted/40 border border-border/80 flex items-center justify-between gap-3 shadow-2xs">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="w-8 h-8 rounded-xl bg-muted text-muted-foreground flex items-center justify-center shrink-0 border border-border/60">
                        <MinusCircle className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs font-bold text-muted-foreground">Unanswered</div>
                        <div className="text-[10px] text-muted-foreground font-medium">{unattemptedPct}% of questions</div>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-base font-black text-foreground">
                        {attempt.unattempted_count} <span className="text-xs font-semibold text-muted-foreground">/ {totalQuestions}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Desktop View: 3 Compact Cards */}
                <div className="hidden sm:grid sm:grid-cols-3 gap-3">
                  {/* Correct Card */}
                  <div className="p-3.5 rounded-xl bg-card/70 backdrop-blur-glass border border-secondary/30 hover:border-secondary/60 shadow-glass-xs transition-all space-y-2 group">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-bold text-secondary flex items-center gap-1.5">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Correct
                      </span>
                      <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded bg-secondary/15 text-secondary border border-secondary/25">
                        {correctPct}%
                      </span>
                    </div>

                    <div className="flex items-baseline justify-between gap-1">
                      <span className="text-2xl font-extrabold text-foreground tracking-tight">
                        {attempt.correct_count}
                      </span>
                      <span className="text-[10px] text-muted-foreground font-medium">
                        {attempt.correct_count}/{totalQuestions} Qs
                      </span>
                    </div>

                    <div className="h-1 w-full bg-secondary/15 rounded-full overflow-hidden">
                      <div
                        className="bg-secondary h-full rounded-full transition-all duration-500"
                        style={{ width: `${correctPct}%` }}
                      />
                    </div>
                  </div>

                  {/* Incorrect Card */}
                  <div className="p-3.5 rounded-xl bg-card/70 backdrop-blur-glass border border-destructive/30 hover:border-destructive/60 shadow-glass-xs transition-all space-y-2 group">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-bold text-destructive flex items-center gap-1.5">
                        <XCircle className="h-3.5 w-3.5" /> Incorrect
                      </span>
                      <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded bg-destructive/15 text-destructive border border-destructive/25">
                        {incorrectPct}%
                      </span>
                    </div>

                    <div className="flex items-baseline justify-between gap-1">
                      <span className="text-2xl font-extrabold text-foreground tracking-tight">
                        {attempt.incorrect_count}
                      </span>
                      <span className="text-[10px] text-muted-foreground font-medium">
                        {attempt.incorrect_count}/{totalQuestions} Qs
                      </span>
                    </div>

                    <div className="h-1 w-full bg-destructive/15 rounded-full overflow-hidden">
                      <div
                        className="bg-destructive h-full rounded-full transition-all duration-500"
                        style={{ width: `${incorrectPct}%` }}
                      />
                    </div>
                  </div>

                  {/* Unanswered Card */}
                  <div className="p-3.5 rounded-xl bg-card/70 backdrop-blur-glass border border-border/80 hover:border-border shadow-glass-xs transition-all space-y-2 group">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-bold text-muted-foreground flex items-center gap-1.5">
                        <MinusCircle className="h-3.5 w-3.5" /> Unanswered
                      </span>
                      <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded bg-muted/60 text-muted-foreground border border-border/60">
                        {unattemptedPct}%
                      </span>
                    </div>

                    <div className="flex items-baseline justify-between gap-1">
                      <span className="text-2xl font-extrabold text-foreground tracking-tight">
                        {attempt.unattempted_count}
                      </span>
                      <span className="text-[10px] text-muted-foreground font-medium">
                        {attempt.unattempted_count}/{totalQuestions} Qs
                      </span>
                    </div>

                    <div className="h-1 w-full bg-muted rounded-full overflow-hidden">
                      <div
                        className="bg-muted-foreground/40 h-full rounded-full transition-all duration-500"
                        style={{ width: `${unattemptedPct}%` }}
                      />
                    </div>
                  </div>
                </div>

                {/* Compact Stacked Distribution Strip */}
                <div className="p-3 sm:p-3.5 rounded-xl bg-muted/30 border border-border/40 space-y-2.5">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 text-xs font-bold">
                    <span className="text-muted-foreground uppercase text-[10px] tracking-wider">Response Distribution</span>
                    <span className="text-foreground text-[11px] font-semibold">{attempt.correct_count + attempt.incorrect_count} Answered • {attempt.unattempted_count} Unanswered</span>
                  </div>

                  {/* Multi-segment visual bar */}
                  <div className="h-2 w-full rounded-full bg-muted overflow-hidden flex shadow-2xs">
                    {attempt.correct_count > 0 && (
                      <div
                        style={{ width: `${correctPct}%` }}
                        className="bg-secondary h-full transition-all"
                        title={`Correct: ${attempt.correct_count} (${correctPct}%)`}
                      />
                    )}
                    {attempt.incorrect_count > 0 && (
                      <div
                        style={{ width: `${incorrectPct}%` }}
                        className="bg-destructive h-full transition-all"
                        title={`Incorrect: ${attempt.incorrect_count} (${incorrectPct}%)`}
                      />
                    )}
                    {attempt.unattempted_count > 0 && (
                      <div
                        style={{ width: `${unattemptedPct}%` }}
                        className="bg-muted-foreground/30 h-full transition-all"
                        title={`Unanswered: ${attempt.unattempted_count} (${unattemptedPct}%)`}
                      />
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5 pt-0.5 text-[11px] font-semibold">
                    <span className="flex items-center gap-1.5 text-secondary">
                      <span className="w-2 h-2 rounded-full bg-secondary shrink-0" />
                      <span>{attempt.correct_count} Correct ({correctPct}%)</span>
                    </span>
                    <span className="flex items-center gap-1.5 text-destructive">
                      <span className="w-2 h-2 rounded-full bg-destructive shrink-0" />
                      <span>{attempt.incorrect_count} Incorrect ({incorrectPct}%)</span>
                    </span>
                    <span className="flex items-center gap-1.5 text-muted-foreground">
                      <span className="w-2 h-2 rounded-full bg-muted-foreground/60 shrink-0" />
                      <span>{attempt.unattempted_count} Unanswered ({unattemptedPct}%)</span>
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Performance Insights Sidebar */}
            <Card className="border-border/80 bg-card/60 backdrop-blur-glass shadow-glass-sm rounded-2xl">
              <CardHeader className="pb-3 border-b border-border/40">
                <CardTitle className="text-base font-extrabold flex items-center gap-2 text-foreground">
                  <TrendingUp className="h-4.5 w-4.5 text-tertiary" /> Performance Insights
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div className="p-3.5 rounded-xl bg-primary/10 border border-primary/20 space-y-1">
                  <p className="text-xs font-bold text-primary flex items-center gap-1.5">
                    <ShieldCheck className="h-4 w-4" /> Speed Metric
                  </p>
                  <p className="text-xs text-foreground font-medium">
                    Average time per question: <strong>{totalQuestions ? Math.round(attempt.time_spent_seconds / totalQuestions) : 0} seconds</strong>.
                  </p>
                </div>

                <div className="p-3.5 rounded-xl bg-secondary/10 border border-secondary/20 space-y-1">
                  <p className="text-xs font-bold text-secondary flex items-center gap-1.5">
                    <Target className="h-4 w-4" /> Accuracy Threshold
                  </p>
                  <p className="text-xs text-foreground font-medium">
                    {attempt.accuracy >= 75
                      ? 'Outstanding precision! You are well above target cutoff guidelines.'
                      : 'Aim for > 75% accuracy to maximize your entrance test rank.'}
                  </p>
                </div>

                <Button
                  variant="outline"
                  className="w-full font-bold text-xs mt-2 rounded-xl h-10 border-primary/40 text-primary hover:bg-primary/10"
                  onClick={() => setActiveTab('questions')}
                >
                  Review Detailed Solutions →
                </Button>
              </CardContent>
            </Card>

            {/* Section-wise Performance Summary (when test has sections) */}
            {hasSections && section_breakdown && section_breakdown.length > 0 && (
              <Card className="md:col-span-3 border-border/80 bg-card/60 backdrop-blur-glass shadow-glass-sm rounded-2xl overflow-hidden">
                <CardHeader className="p-4 sm:p-6 pb-3 sm:pb-3 bg-muted/20 border-b border-border/40 flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
                  <CardTitle className="text-sm sm:text-base font-extrabold flex items-center gap-2 text-foreground">
                    <Layers className="h-4 w-4 sm:h-4.5 sm:w-4.5 text-primary" /> Section-wise Performance Summary
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setActiveTab('breakdown')}
                    className="w-full sm:w-auto text-xs font-bold h-8 rounded-xl border-primary/30 text-primary hover:bg-primary/10 cursor-pointer"
                  >
                    View Section Breakdown →
                  </Button>
                </CardHeader>
                <CardContent className="p-3 sm:p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                    {section_breakdown.map((sec) => (
                      <div key={sec.id} className="p-3.5 sm:p-4 rounded-2xl bg-card border border-border/70 shadow-2xs space-y-3 flex flex-col justify-between">
                        <div className="space-y-1">
                          <div className="flex items-center justify-between gap-2">
                            <h4 className="font-extrabold text-sm text-foreground flex items-center gap-1.5 min-w-0">
                              <Layers className="h-3.5 w-3.5 text-primary shrink-0" />
                              <span className="truncate">{sec.name}</span>
                            </h4>
                            <Badge
                              variant={sec.accuracy >= 70 ? 'secondary' : 'outline'}
                              className="font-extrabold text-[10px] sm:text-[11px] px-2 py-0.5 shrink-0 whitespace-nowrap"
                            >
                              {formatNumber(sec.accuracy)}% Acc
                            </Badge>
                          </div>
                          {sec.subject_hint && sec.subject_hint.trim().toLowerCase() !== sec.name.trim().toLowerCase() && (
                            <p className="text-[11px] text-muted-foreground font-medium pl-5">{sec.subject_hint}</p>
                          )}
                        </div>

                        <div className="flex items-baseline justify-between border-y border-border/40 py-2 text-xs">
                          <span className="text-muted-foreground font-medium">Score</span>
                          <span className="font-extrabold text-sm text-foreground">
                            {formatNumber(sec.score)} <span className="text-muted-foreground text-xs font-normal">/ {formatNumber(sec.max_score)} pts</span>
                          </span>
                        </div>

                        <div className="grid grid-cols-3 gap-1.5 text-center text-[11px] font-bold">
                          <div className="p-1.5 rounded-xl bg-secondary/10 text-secondary border border-secondary/20">
                            <div className="text-sm font-black">{sec.correct_count}</div>
                            <div className="text-[9px] uppercase tracking-tight font-bold">Correct</div>
                          </div>
                          <div className="p-1.5 rounded-xl bg-destructive/10 text-destructive border border-destructive/20">
                            <div className="text-sm font-black">{sec.incorrect_count}</div>
                            <div className="text-[9px] uppercase tracking-tight font-bold">Wrong</div>
                          </div>
                          <div className="p-1.5 rounded-xl bg-muted/60 text-muted-foreground border border-border/60">
                            <div className="text-sm font-black">{sec.unattempted_count}</div>
                            <div className="text-[9px] uppercase tracking-tight font-bold">Skipped</div>
                          </div>
                        </div>

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSectionFilter(sec.id.toString())
                            applyServerFilters({ section_id: sec.id.toString() }, 1, pageSize)
                            setActiveTab('questions')
                          }}
                          className="w-full h-8 text-xs font-bold rounded-xl border-border hover:border-primary/40 hover:text-primary transition-all cursor-pointer"
                        >
                          View Solutions ({sec.total_questions}) →
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* TAB 2: SECTION / SUBJECT BREAKDOWN */}
        {activeTab === 'breakdown' && (
          <div className="space-y-6 animate-fade-in">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Overall Response Pie / Donut Chart */}
              <Card className="md:col-span-1 shadow-glass-sm border-glass-border">
                <CardHeader className="pb-2 border-b border-border/40">
                  <CardTitle className="text-base font-extrabold flex items-center gap-2">
                    <Target className="h-4.5 w-4.5 text-primary" /> Overall Share
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 flex flex-col items-center justify-center">
                  <div className="w-full h-64 relative flex items-center justify-center">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Correct', value: attempt.correct_count, color: 'hsl(var(--secondary))' },
                            { name: 'Incorrect', value: attempt.incorrect_count, color: 'hsl(var(--destructive))' },
                            { name: 'Unanswered', value: attempt.unattempted_count, color: 'hsl(var(--muted-foreground))' },
                          ].filter(d => d.value > 0)}
                          cx="50%"
                          cy="50%"
                          innerRadius={65}
                          outerRadius={85}
                          paddingAngle={4}
                          dataKey="value"
                        >
                          {[
                            { name: 'Correct', value: attempt.correct_count, color: 'hsl(var(--secondary))' },
                            { name: 'Incorrect', value: attempt.incorrect_count, color: 'hsl(var(--destructive))' },
                            { name: 'Unanswered', value: attempt.unattempted_count, color: 'hsl(var(--muted-foreground))' },
                          ].filter(d => d.value > 0).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} stroke="transparent" />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'hsl(var(--card))',
                            borderColor: 'hsl(var(--border))',
                            borderRadius: '12px',
                            color: 'hsl(var(--foreground))',
                            fontSize: '12px'
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="absolute flex flex-col items-center justify-center pointer-events-none">
                      <span className="text-3xl font-extrabold text-foreground">{formatNumber(attempt.accuracy)}%</span>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Accuracy</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-center gap-4 text-xs font-bold pt-2">
                    <span className="flex items-center gap-1.5 text-secondary">
                      <span className="w-2.5 h-2.5 rounded-full bg-secondary" /> Correct ({attempt.correct_count})
                    </span>
                    <span className="flex items-center gap-1.5 text-destructive">
                      <span className="w-2.5 h-2.5 rounded-full bg-destructive" /> Incorrect ({attempt.incorrect_count})
                    </span>
                    <span className="flex items-center gap-1.5 text-muted-foreground">
                      <span className="w-2.5 h-2.5 rounded-full bg-muted-foreground" /> Unanswered ({attempt.unattempted_count})
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Performance Breakdown: Section-wise (if sections exist) or Subject-wise */}
              <Card className="md:col-span-2 shadow-glass-sm border-glass-border">
                <CardHeader className="pb-2 border-b border-border/40">
                  <CardTitle className="text-base font-extrabold flex items-center gap-2">
                    {hasSections ? (
                      <><Layers className="h-4.5 w-4.5 text-primary" /> Section-wise Performance Breakdown</>
                    ) : (
                      <><TrendingUp className="h-4.5 w-4.5 text-primary" /> Subject-wise Performance Breakdown</>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  {hasSections && section_breakdown && section_breakdown.length > 0 ? (
                    section_breakdown.map((sec) => {
                      const totalSecQ = sec.total_questions || (sec.correct_count + sec.incorrect_count + sec.unattempted_count)
                      const secAcc = totalSecQ > 0 ? Math.round((sec.correct_count / (sec.correct_count + sec.incorrect_count || 1)) * 100) : 0

                      return (
                        <div key={sec.id} className="p-4 rounded-2xl bg-muted/40 border border-border/40 space-y-3">
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            <div>
                              <span className="font-extrabold text-sm text-foreground flex items-center gap-2">
                                <Layers className="h-4 w-4 text-primary" /> {sec.name}
                              </span>
                              {sec.subject_hint && sec.subject_hint.trim().toLowerCase() !== sec.name.trim().toLowerCase() && (
                                <p className="text-xs text-muted-foreground mt-0.5">{sec.subject_hint}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <Badge variant="outline" className="text-[10px] sm:text-[11px] font-bold font-mono">
                                Score: {formatNumber(sec.score)} / {formatNumber(sec.max_score)} pts
                              </Badge>
                              <Badge variant={secAcc >= 70 ? 'secondary' : 'outline'} className="font-extrabold text-[10px] sm:text-xs">
                                {secAcc}% Accuracy
                              </Badge>
                            </div>
                          </div>

                          {/* Stacked Progress Bar */}
                          <div className="space-y-1">
                            <div className="h-3 w-full rounded-full bg-muted overflow-hidden flex shadow-2xs">
                              {sec.correct_count > 0 && (
                                <div
                                  style={{ width: `${(sec.correct_count / totalSecQ) * 100}%` }}
                                  className="bg-secondary h-full transition-all"
                                  title={`Correct: ${sec.correct_count}`}
                                />
                              )}
                              {sec.incorrect_count > 0 && (
                                <div
                                  style={{ width: `${(sec.incorrect_count / totalSecQ) * 100}%` }}
                                  className="bg-destructive h-full transition-all"
                                  title={`Incorrect: ${sec.incorrect_count}`}
                                />
                              )}
                              {sec.unattempted_count > 0 && (
                                <div
                                  style={{ width: `${(sec.unattempted_count / totalSecQ) * 100}%` }}
                                  className="bg-muted-foreground/40 h-full transition-all"
                                  title={`Skipped: ${sec.unattempted_count}`}
                                />
                              )}
                            </div>
                            <div className="flex justify-between text-[11px] font-bold text-muted-foreground pt-1 flex-wrap gap-1">
                              <span className="text-secondary">{sec.correct_count} Correct</span>
                              <span className="text-destructive">{sec.incorrect_count} Incorrect</span>
                              <span>{sec.unattempted_count} Skipped</span>
                              <span className="text-foreground">{totalSecQ} Total Qs</span>
                            </div>
                          </div>

                          <div className="flex items-center justify-between pt-1 border-t border-border/30 text-xs">
                            <span className="text-[11px] text-muted-foreground font-mono">
                              Marking: +{sec.positive_marks ?? 4} / -{sec.negative_marks ?? 1}
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSectionFilter(sec.id.toString())
                                applyServerFilters({ section_id: sec.id.toString() }, 1, pageSize)
                                setActiveTab('questions')
                              }}
                              className="h-7 text-xs font-bold text-primary hover:bg-primary/10 cursor-pointer"
                            >
                              Explore {sec.name} Questions →
                            </Button>
                          </div>
                        </div>
                      )
                    })
                  ) : breakdownData.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-12">No subject breakdown metrics recorded.</p>
                  ) : (
                    breakdownData.map((sData) => {
                      const totalSubjectQ = sData.total || (sData.correct + sData.incorrect + sData.unattempted)
                      const subjectAcc = totalSubjectQ > 0 ? Math.round((sData.correct / totalSubjectQ) * 100) : 0

                      return (
                        <div key={sData.subject} className="p-4 rounded-2xl bg-muted/40 border border-border/40 space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="font-extrabold text-sm text-foreground flex items-center gap-2">
                              <BookOpen className="h-4 w-4 text-primary" /> {sData.subject}
                            </span>
                            <Badge variant={subjectAcc >= 70 ? 'secondary' : 'outline'} className="font-extrabold text-xs">
                              {subjectAcc}% Accuracy
                            </Badge>
                          </div>

                          {/* Stacked Progress Bar */}
                          <div className="space-y-1">
                            <div className="h-3 w-full rounded-full bg-muted overflow-hidden flex shadow-2xs">
                              {sData.correct > 0 && (
                                <div
                                  style={{ width: `${(sData.correct / totalSubjectQ) * 100}%` }}
                                  className="bg-secondary h-full transition-all"
                                  title={`Correct: ${sData.correct}`}
                                />
                              )}
                              {sData.incorrect > 0 && (
                                <div
                                  style={{ width: `${(sData.incorrect / totalSubjectQ) * 100}%` }}
                                  className="bg-destructive h-full transition-all"
                                  title={`Incorrect: ${sData.incorrect}`}
                                />
                              )}
                              {sData.unattempted > 0 && (
                                <div
                                  style={{ width: `${(sData.unattempted / totalSubjectQ) * 100}%` }}
                                  className="bg-muted-foreground/40 h-full transition-all"
                                  title={`Unanswered: ${sData.unattempted}`}
                                />
                              )}
                            </div>
                            <div className="flex justify-between text-[11px] font-bold text-muted-foreground pt-1">
                              <span className="text-secondary">{sData.correct} Correct</span>
                              <span className="text-destructive">{sData.incorrect} Incorrect</span>
                              <span>{sData.unattempted} Unanswered</span>
                              <span className="text-foreground">{totalSubjectQ} Total</span>
                            </div>
                          </div>
                        </div>
                      )
                    })
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* TAB 3: QUESTION SOLUTIONS */}
        {activeTab === 'questions' && (
          <div className="space-y-5 animate-fade-in relative">
            {/* Filter Control Bar */}
            <Card className="p-4 sm:p-5 space-y-3.5 shadow-glass-sm border-border bg-card/80 backdrop-blur-glass">
              {/* Header Row: Title + Filter Counter + Search + Expand All / Collapse All */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-border/40 pb-3">
                {/* Title & Count Badge */}
                <div className="flex items-center gap-2 min-w-0">
                  <SlidersHorizontal className="h-4.5 w-4.5 text-primary shrink-0" />
                  <h2 className="font-extrabold text-base text-foreground truncate">Filter Solutions</h2>
                  <Badge variant="outline" className="text-[11px] font-bold font-mono shrink-0 bg-muted/40">
                    {totalFilteredCount} of {totalTestQuestions}
                  </Badge>
                  {loadingQuestions && (
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-primary ml-1 shrink-0" />
                  )}
                </div>

                {/* Right: Search Bar & Expand/Collapse Toggle */}
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <div className="relative flex-1 sm:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={handleSearchImmediate}
                      placeholder="Search question or solution..."
                      className="w-full h-9 rounded-xl bg-background border border-input pl-8 pr-7 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => {
                          setSearchQuery('')
                          applyServerFilters({ search: '' }, 1, pageSize)
                        }}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground hover:text-foreground cursor-pointer"
                      >
                        ×
                      </button>
                    )}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={toggleAllQuestions}
                    className="h-9 px-3 text-xs font-bold rounded-xl border-border hover:bg-muted/40 cursor-pointer shrink-0"
                    title={areAllExpanded ? 'Collapse all questions' : 'Expand all questions'}
                  >
                    {areAllExpanded ? (
                      <>
                        <ChevronsUp className="h-3.5 w-3.5 mr-1 text-primary" />
                        <span>Collapse All</span>
                      </>
                    ) : (
                      <>
                        <ChevronsDown className="h-3.5 w-3.5 mr-1 text-primary" />
                        <span>Expand All</span>
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Status Quick Filter Pills */}
              <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5 scrollbar-none">
                <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground shrink-0 hidden sm:flex items-center gap-1">
                  Status:
                </span>
                {[
                  { id: 'all', label: 'All', count: totalTestQuestions },
                  { id: 'answered', label: 'Answered', count: attempt.correct_count + attempt.incorrect_count, dotColor: 'bg-primary' },
                  { id: 'unanswered', label: 'Unanswered', count: attempt.unattempted_count, dotColor: 'bg-muted-foreground' },
                  { id: 'correct', label: 'Correct', count: attempt.correct_count, dotColor: 'bg-secondary' },
                  { id: 'incorrect', label: 'Incorrect', count: attempt.incorrect_count, dotColor: 'bg-destructive' }
                ].map((st) => (
                  <button
                    key={st.id}
                    type="button"
                    onClick={() => {
                      const next = statusFilter === st.id ? 'all' : st.id
                      setStatusFilter(next)
                      applyServerFilters({ status: next }, 1, pageSize)
                    }}
                    className={`px-3 py-1 rounded-xl text-xs font-bold transition-all shrink-0 flex items-center gap-1.5 cursor-pointer ${
                      statusFilter === st.id
                        ? 'bg-primary text-primary-fg shadow-2xs'
                        : 'bg-card text-foreground hover:bg-muted/60 border border-border/70'
                    }`}
                  >
                    {st.dotColor && <span className={`w-2 h-2 rounded-full ${st.dotColor}`} />}
                    <span>{st.label}</span>
                    <Badge variant="outline" className="text-[9px] px-1 py-0 bg-background/50 text-current border-current/30">
                      {st.count}
                    </Badge>
                  </button>
                ))}
              </div>

              {/* Section Quick Filter Pills Bar (when test has sections) */}
              {hasSections && section_breakdown && section_breakdown.length > 0 && (
                <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5 scrollbar-none">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground shrink-0 hidden sm:flex items-center gap-1">
                    <Layers className="h-3 w-3 text-primary" /> Section:
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setSectionFilter('all')
                      applyServerFilters({ section_id: 'all' }, 1, pageSize)
                    }}
                    className={`px-3 py-1 rounded-xl text-xs font-bold transition-all shrink-0 cursor-pointer ${
                      sectionFilter === 'all'
                        ? 'bg-primary text-primary-fg shadow-2xs'
                        : 'bg-card text-foreground hover:bg-muted/60 border border-border/70'
                    }`}
                  >
                    All Sections ({totalTestQuestions})
                  </button>
                  {section_breakdown.map((sec) => (
                    <button
                      key={sec.id}
                      type="button"
                      onClick={() => {
                        const next = sectionFilter === String(sec.id) ? 'all' : String(sec.id)
                        setSectionFilter(next)
                        applyServerFilters({ section_id: next }, 1, pageSize)
                      }}
                      className={`px-3 py-1 rounded-xl text-xs font-bold transition-all shrink-0 flex items-center gap-1.5 cursor-pointer ${
                        sectionFilter === String(sec.id)
                          ? 'bg-primary text-primary-fg shadow-2xs'
                          : 'bg-card text-foreground hover:bg-muted/60 border border-border/70'
                      }`}
                    >
                      <span>{sec.name}</span>
                      <Badge variant="outline" className="text-[9px] px-1 py-0 bg-background/50 text-current border-current/30">
                        {sec.total_questions}
                      </Badge>
                    </button>
                  ))}
                </div>
              )}

              {/* Secondary Filters: Difficulty, Question Type & Reset */}
              <div className="flex flex-wrap items-center justify-between gap-3 pt-1 border-t border-border/40">
                <div className="flex flex-wrap items-center gap-2">
                  {/* Difficulty Filter Dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 px-3 rounded-xl bg-background border-input hover:bg-muted/30 text-xs font-semibold text-foreground focus:ring-2 focus:ring-primary cursor-pointer gap-1.5"
                      >
                        <span className="text-[10px] text-muted-foreground uppercase font-bold">Difficulty:</span>
                        <span className="capitalize">
                          {difficultyFilter === 'all'
                            ? 'All'
                            : difficultyFilter}
                        </span>
                        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground ml-0.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-36 bg-card border-border shadow-glass-md rounded-xl p-1 z-50">
                      {[
                        { value: 'all', label: 'All Difficulties' },
                        { value: 'easy', label: 'Easy' },
                        { value: 'medium', label: 'Medium' },
                        { value: 'hard', label: 'Hard' }
                      ].map((item) => (
                        <DropdownMenuItem
                          key={item.value}
                          onClick={() => {
                            setDifficultyFilter(item.value)
                            applyServerFilters({ difficulty: item.value }, 1, pageSize)
                          }}
                          className={`flex items-center justify-between px-3 py-1.5 text-xs font-semibold rounded-lg cursor-pointer transition-colors ${
                            difficultyFilter === item.value
                              ? 'bg-primary/10 text-primary font-bold'
                              : 'text-foreground hover:bg-muted/40'
                          }`}
                        >
                          <span>{item.label}</span>
                          {difficultyFilter === item.value && <Check className="h-3.5 w-3.5 text-primary" />}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* Question Type Filter Dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 px-3 rounded-xl bg-background border-input hover:bg-muted/30 text-xs font-semibold text-foreground focus:ring-2 focus:ring-primary cursor-pointer gap-1.5"
                      >
                        <span className="text-[10px] text-muted-foreground uppercase font-bold">Type:</span>
                        <span>
                          {typeFilter === 'all'
                            ? 'All'
                            : typeFilter === 'mcq'
                              ? 'MCQ'
                              : typeFilter === 'multi_correct'
                                ? 'Multi-Correct'
                                : 'Numerical'}
                        </span>
                        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground ml-0.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-40 bg-card border-border shadow-glass-md rounded-xl p-1 z-50">
                      {[
                        { value: 'all', label: 'All Types' },
                        { value: 'mcq', label: 'MCQ' },
                        { value: 'multi_correct', label: 'Multi-Correct (MSQ)' },
                        { value: 'numerical', label: 'Numerical' }
                      ].map((item) => (
                        <DropdownMenuItem
                          key={item.value}
                          onClick={() => {
                            setTypeFilter(item.value)
                            applyServerFilters({ question_type: item.value }, 1, pageSize)
                          }}
                          className={`flex items-center justify-between px-3 py-1.5 text-xs font-semibold rounded-lg cursor-pointer transition-colors ${
                            typeFilter === item.value
                              ? 'bg-primary/10 text-primary font-bold'
                              : 'text-foreground hover:bg-muted/40'
                          }`}
                        >
                          <span>{item.label}</span>
                          {typeFilter === item.value && <Check className="h-3.5 w-3.5 text-primary" />}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Reset Filters action */}
                {activeFiltersCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearAllFilters}
                    className="h-8 px-2 text-xs font-bold text-destructive hover:bg-destructive/10 gap-1 cursor-pointer"
                  >
                    <RotateCcw className="h-3.5 w-3.5" /> Reset Filters
                  </Button>
                )}
              </div>
            </Card>

            {/* Empty State */}
            {totalFilteredCount === 0 ? (
              <Card className="p-12 text-center space-y-3">
                <Filter className="h-10 w-10 text-muted-foreground mx-auto" />
                <h3 className="font-extrabold text-base text-foreground">No questions match your selected filters</h3>
                <p className="text-xs text-muted-foreground">Try clearing your search term or selecting 'All' filters above.</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSectionFilter('all')
                    setStatusFilter('all')
                    setDifficultyFilter('all')
                    setTypeFilter('all')
                    setSearchQuery('')
                    applyServerFilters({ section_id: 'all', status: 'all', difficulty: 'all', question_type: 'all', search: '' }, 1, pageSize)
                  }}
                  className="rounded-xl font-bold border-primary/40 text-primary hover:bg-primary/10"
                >
                  Reset All Filters
                </Button>
              </Card>
            ) : (
              <>
                {/* MOBILE VIEW: Infinite Scroll with 5-Question Prefetch Buffer */}
                <div className="block sm:hidden space-y-4">
                  {mobileQuestions.map((q, idx) => {
                    const questionNum = q.question_number ?? (idx + 1)
                    const isCorrect = q.is_correct === true
                    const isIncorrect = q.is_correct === false
                    const isUnattempted = q.is_correct === null
                    const isExpanded = Boolean(expandedQuestions[q.id])
                    const correctOpt = q.options?.find(o => o.is_correct)

                    return (
                      <Card
                        key={q.id}
                        className={`border-l-4 transition-all ${
                          isCorrect
                            ? 'border-l-secondary bg-card'
                            : isIncorrect
                              ? 'border-l-destructive bg-card'
                              : 'border-l-border bg-card'
                        }`}
                      >
                        <CardContent className="p-4 space-y-3">
                          {/* Header / Accordion Toggle */}
                          <div
                            onClick={() => toggleQuestion(q.id)}
                            className="space-y-2 cursor-pointer select-none group"
                          >
                            <div className="flex items-center justify-between gap-2 text-xs">
                              <div className="flex items-center gap-2 flex-wrap min-w-0">
                                <span className="font-bold text-sm text-foreground group-hover:text-primary transition-colors">
                                  Question {questionNum}
                                </span>

                                {(q.section_name || q.primary_subject) && (
                                  <span className="text-[11px] text-muted-foreground font-medium truncate max-w-[140px]">
                                    {q.section_name || q.primary_subject}
                                  </span>
                                )}

                                <span className="text-[9px] font-medium text-muted-foreground bg-muted/60 px-1.5 py-0.5 rounded-md border border-border/40">
                                  {q.difficulty}
                                </span>
                              </div>

                              <div className="flex items-center gap-1.5 shrink-0">
                                {isCorrect && (
                                  <span className="inline-flex items-center gap-1 text-[11px] font-bold text-secondary bg-secondary/10 px-2 py-0.5 rounded-full border border-secondary/25">
                                    <CheckCircle2 className="h-3 w-3" /> +{q.marks_awarded} pts
                                  </span>
                                )}
                                {isIncorrect && (
                                  <span className="inline-flex items-center gap-1 text-[11px] font-bold text-destructive bg-destructive/10 px-2 py-0.5 rounded-full border border-destructive/25">
                                    <XCircle className="h-3 w-3" /> {q.marks_awarded != null && Number(q.marks_awarded) !== 0 ? `${q.marks_awarded} pts` : 'Incorrect'}
                                  </span>
                                )}
                                {isUnattempted && (
                                  <span className="inline-flex items-center text-[10px] font-medium text-muted-foreground bg-muted/60 px-2 py-0.5 rounded-full border border-border/50">
                                    Unanswered
                                  </span>
                                )}
                                <div className="p-0.5 rounded text-muted-foreground group-hover:text-primary group-hover:bg-muted/50 transition-colors">
                                  {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                                </div>
                              </div>
                            </div>

                            {/* Clean Stats Row: Timing & Cohort Accuracy */}
                            <div className="flex items-center gap-2 text-[10px] text-muted-foreground flex-wrap">
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3 text-muted-foreground/70" />
                                <span>Time:</span>
                                <strong className={`font-semibold ${
                                  q.time_spent_seconds && q.target_time_seconds && q.time_spent_seconds > q.target_time_seconds * 1.5
                                    ? 'text-destructive'
                                    : 'text-foreground'
                                }`}>
                                  {q.time_spent_seconds != null && q.time_spent_seconds > 0 ? `${q.time_spent_seconds}s` : '0s'}
                                </strong>
                                {q.stats?.avg_time_spent_seconds != null && (
                                  <span className="text-muted-foreground/60">(Avg: {q.stats.avg_time_spent_seconds}s)</span>
                                )}
                              </span>

                              {q.stats?.accuracy_percentage != null && (
                                <>
                                  <span className="text-muted-foreground/40">·</span>
                                  <span>
                                    Accuracy: <strong className="font-semibold text-foreground">{q.stats.accuracy_percentage}%</strong>
                                  </span>
                                </>
                              )}
                            </div>

                            {/* Collapsed Preview */}
                            {!isExpanded && (
                              <div className="text-xs text-muted-foreground line-clamp-1 font-medium select-none pt-0.5">
                                <KatexRenderer text={q.question_text.slice(0, 140)} />
                              </div>
                            )}
                          </div>

                          {/* Expanded Content */}
                          {isExpanded && (
                            <div className="space-y-3 pt-2 border-t border-border/40">
                              {/* Question Context if present */}
                              {q.question_context && (
                                <QuestionContextCard
                                  context={q.question_context}
                                  format={q.render_format}
                                  className="mb-2"
                                />
                              )}

                              {/* Question Text (clean, no duplicate passage) */}
                              <div className="text-xs sm:text-sm font-semibold leading-relaxed text-foreground">
                                <KatexRenderer text={q.question_context ? (parseQuestionDirectionAndPrompt(q.question_text).prompt || q.question_text) : q.question_text} />
                              </div>

                              {/* Question Figure / Diagram */}
                              {q.figure_image_url && (
                                <div
                                  onClick={() => window.open(q.figure_image_url!, '_blank')}
                                  className="my-2.5 p-3 rounded-xl border border-border/70 bg-muted/20 flex flex-col items-center cursor-pointer group/img hover:border-primary/50 transition-colors"
                                >
                                  <img
                                    src={q.figure_image_url}
                                    alt="Question figure"
                                    className="max-h-72 w-auto max-w-full object-contain rounded-lg border border-border/50"
                                  />
                                  <span className="text-[10px] text-muted-foreground mt-1.5 font-medium flex items-center gap-1 group-hover/img:text-primary transition-colors">
                                    <Maximize2 className="h-2.5 w-2.5" /> Tap image to view in new tab
                                  </span>
                                </div>
                              )}

                              {/* Question Text Image */}
                              {q.question_image_url && (
                                <div
                                  onClick={() => window.open(q.question_image_url!, '_blank')}
                                  className="my-2.5 p-3 rounded-xl border border-border/70 bg-muted/20 flex flex-col items-center cursor-pointer group/img hover:border-primary/50 transition-colors"
                                >
                                  <img
                                    src={q.question_image_url}
                                    alt="Question image"
                                    className="max-h-72 w-auto max-w-full object-contain rounded-lg border border-border/50"
                                  />
                                  <span className="text-[10px] text-muted-foreground mt-1.5 font-medium flex items-center gap-1 group-hover/img:text-primary transition-colors">
                                    <Maximize2 className="h-2.5 w-2.5" /> Tap image to view in new tab
                                  </span>
                                </div>
                              )}

                              {/* Quick Answer Banner for incorrect or unattempted MCQ questions */}
                              {!isCorrect && correctOpt && (
                                <div className="flex items-center justify-between p-2.5 rounded-xl bg-secondary/10 border border-secondary/25 text-xs">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <CheckCircle2 className="h-4 w-4 text-secondary shrink-0" />
                                    <span className="font-semibold text-secondary truncate">
                                      Correct Answer: <strong className="font-extrabold underline decoration-secondary/40">Option {correctOpt.label}</strong>
                                    </span>
                                  </div>
                                  {isIncorrect && q.selected_option_id && (
                                    <span className="text-[10px] text-destructive font-bold bg-destructive/10 px-2 py-0.5 rounded-full shrink-0 border border-destructive/20">
                                      You chose {q.options?.find(o => o.id === q.selected_option_id)?.label ? `Option ${q.options.find(o => o.id === q.selected_option_id)?.label}` : 'different'}
                                    </span>
                                  )}
                                </div>
                              )}

                              {/* MCQ Options list */}
                              {q.options && q.options.length > 0 && (
                                <div className="space-y-2 pt-1">
                                  {q.options.map((opt) => {
                                    const isSelected = opt.id === q.selected_option_id
                                    const isOptCorrect = opt.is_correct

                                    let containerStyle = 'border-border/60 bg-card'
                                    let labelStyle = 'bg-muted text-muted-foreground border border-border/60'
                                    let badgeContent = null

                                    if (isOptCorrect && isSelected) {
                                      containerStyle = 'border-secondary/40 bg-secondary/8 shadow-2xs'
                                      labelStyle = 'bg-secondary text-secondary-fg font-extrabold'
                                      badgeContent = (
                                        <span className="flex items-center gap-1 text-[10px] font-bold text-secondary bg-secondary/15 px-2 py-0.5 rounded-full border border-secondary/30 whitespace-nowrap">
                                          <CheckCircle2 className="h-3 w-3" /> ✓ Correct & Yours
                                        </span>
                                      )
                                    } else if (isOptCorrect) {
                                      containerStyle = 'border-secondary/40 bg-secondary/6 shadow-2xs'
                                      labelStyle = 'bg-secondary text-secondary-fg font-extrabold'
                                      badgeContent = (
                                        <span className="flex items-center gap-1 text-[10px] font-bold text-secondary bg-secondary/15 px-2 py-0.5 rounded-full border border-secondary/30 whitespace-nowrap">
                                          <CheckCircle2 className="h-3 w-3" /> ✓ Correct
                                        </span>
                                      )
                                    } else if (isSelected) {
                                      containerStyle = 'border-destructive/40 bg-destructive/6 shadow-2xs'
                                      labelStyle = 'bg-destructive text-destructive-foreground font-extrabold'
                                      badgeContent = (
                                        <span className="flex items-center gap-1 text-[10px] font-bold text-destructive bg-destructive/15 px-2 py-0.5 rounded-full border border-destructive/25 whitespace-nowrap">
                                          <XCircle className="h-3 w-3" /> ✗ Your Choice
                                        </span>
                                      )
                                    }

                                    return (
                                      <div key={opt.id} className={`p-2.5 rounded-xl border flex flex-col gap-2 ${containerStyle} transition-all`}>
                                        <div className="flex items-start justify-between gap-2">
                                          <div className="flex items-start gap-2.5 min-w-0 flex-1">
                                            <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-bold ${labelStyle}`}>
                                              {opt.label}
                                            </span>
                                            <div className="flex-1 text-xs font-medium leading-relaxed text-foreground overflow-x-auto space-y-1">
                                              {opt.text ? <KatexRenderer text={opt.text} /> : <span>Option {opt.label}</span>}
                                            </div>
                                          </div>
                                          {badgeContent && (
                                            <div className="shrink-0">
                                              {badgeContent}
                                            </div>
                                          )}
                                        </div>
                                        {opt.option_image_url && (
                                          <div className="pl-7">
                                            <img
                                              src={opt.option_image_url}
                                              alt={`Option ${opt.label}`}
                                              onClick={() => window.open(opt.option_image_url!, '_blank')}
                                              className="max-h-36 w-auto max-w-full object-contain rounded-lg border border-border/50 bg-background/50 p-1 cursor-pointer"
                                            />
                                          </div>
                                        )}
                                      </div>
                                    )
                                  })}
                                </div>
                              )}

                              {/* Numerical Response */}
                              {q.question_type === 'NUMERICAL' && (
                                <div className="p-3 rounded-xl bg-muted/40 border border-border/40 text-xs space-y-1">
                                  <div className="flex items-center justify-between">
                                    <span className="text-muted-foreground font-semibold">Your Answer:</span>
                                    <strong className="text-foreground font-extrabold">{q.numerical_response ?? 'No response'}</strong>
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <span className="text-secondary font-semibold">Correct Answer:</span>
                                    <strong className="text-secondary font-extrabold">{q.numerical_answer}</strong>
                                  </div>
                                </div>
                              )}

                              {/* Solution Explanation */}
                              {(q.structured_solution || q.explanation_text || q.explanation_image_url) && (
                                <div className="space-y-3 pt-3 border-t border-border/50">
                                  <div className="flex items-center gap-1.5 text-xs font-bold text-foreground">
                                    <BookOpen className="h-3.5 w-3.5 text-primary" />
                                    <span>Solution & Explanation</span>
                                  </div>
                                  <StructuredSolutionRenderer
                                    solution={q.structured_solution}
                                    fallbackExplanation={q.explanation_text}
                                    format={q.render_format}
                                  />
                                  {q.explanation_image_url && (
                                    <div
                                      onClick={() => window.open(q.explanation_image_url!, '_blank')}
                                      className="p-3 rounded-xl border border-border/70 bg-muted/20 flex flex-col items-center cursor-pointer group/img hover:border-primary/50 transition-colors"
                                    >
                                      <img
                                        src={q.explanation_image_url}
                                        alt="Explanation diagram"
                                        className="max-h-72 w-auto max-w-full object-contain rounded-lg border border-border/50"
                                      />
                                      <span className="text-[10px] text-muted-foreground mt-1.5 font-medium flex items-center gap-1 group-hover/img:text-primary transition-colors">
                                        <Maximize2 className="h-2.5 w-2.5" /> Tap image to view in new tab
                                      </span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    )
                  })}

                  {/* Mobile Infinite Scroll Trigger Button */}
                  {mobileQuestions.length < totalFilteredCount && (
                    <div className="pt-2 text-center">
                      <Button
                        variant="outline"
                        disabled={mobileLoadingMore}
                        onClick={handleLoadMoreMobile}
                        className="w-full h-11 rounded-xl border-2 border-primary/40 text-primary font-bold text-xs hover:bg-primary/10 gap-2"
                      >
                        {mobileLoadingMore ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin text-primary" /> Loading Next 5 Questions...
                          </>
                        ) : (
                          <>
                            Load More Questions ({totalFilteredCount - mobileQuestions.length} remaining)
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </div>

                {/* DESKTOP VIEW: Paginated Question Cards & Page Size Selector */}
                <div className="hidden sm:block space-y-4">
                  {currentDesktopQuestions.map((q, idx) => {
                    const questionNum = q.question_number ?? ((currentPage - 1) * pageSize + idx + 1)
                    const isCorrect = q.is_correct === true
                    const isIncorrect = q.is_correct === false
                    const isUnattempted = q.is_correct === null
                    const isExpanded = Boolean(expandedQuestions[q.id])

                    return (
                      <Card
                        key={q.id}
                        className={`border-l-4 transition-all ${
                          isCorrect
                            ? 'border-l-secondary bg-card'
                            : isIncorrect
                              ? 'border-l-destructive bg-card'
                              : 'border-l-border bg-card'
                        }`}
                      >
                        <CardContent className="p-6 space-y-4">
                          {/* Header / Accordion Trigger */}
                          <div
                            onClick={() => toggleQuestion(q.id)}
                            className="space-y-2.5 cursor-pointer select-none group"
                          >
                            <div className="flex flex-wrap items-center justify-between gap-3 text-xs">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="font-bold text-sm text-foreground group-hover:text-primary transition-colors">Question {questionNum}</span>

                                {(q.section_name || q.primary_subject) && (
                                  <span className="text-xs text-muted-foreground font-medium flex items-center gap-1">
                                    <span className="text-foreground font-semibold">{q.section_name || q.primary_subject}</span>
                                    {q.primary_chapter && (
                                      <>
                                        <span className="opacity-40">/</span>
                                        <span>{q.primary_chapter}</span>
                                      </>
                                    )}
                                  </span>
                                )}

                                <span className="text-[10px] font-medium text-muted-foreground bg-muted/60 px-2 py-0.5 rounded-md border border-border/40 uppercase">
                                  {q.difficulty}
                                </span>

                                <span className="font-mono text-[10px] font-semibold text-muted-foreground bg-muted/40 px-1.5 py-0.5 rounded-md border border-border/30">
                                  {q.question_type}
                                </span>

                                {q.is_pyq && (
                                  <span className="text-[10px] font-semibold text-muted-foreground bg-muted/40 border border-border/50 px-2 py-0.5 rounded-md flex items-center gap-1">
                                    <GraduationCap className="h-3 w-3 text-muted-foreground shrink-0" />
                                    <span>PYQ {q.pyq_info?.year ? q.pyq_info.year : ''}{q.pyq_info?.exam_name ? ` • ${q.pyq_info.exam_name}` : ''}</span>
                                  </span>
                                )}
                              </div>

                              <div className="flex items-center gap-2">
                                {isCorrect && (
                                  <span className="inline-flex items-center gap-1 text-xs font-bold text-secondary bg-secondary/10 px-2.5 py-1 rounded-full border border-secondary/25">
                                    <CheckCircle2 className="h-3.5 w-3.5" /> +{q.marks_awarded} pts (Correct)
                                  </span>
                                )}
                                {isIncorrect && (
                                  <span className="inline-flex items-center gap-1 text-xs font-bold text-destructive bg-destructive/10 px-2.5 py-1 rounded-full border border-destructive/25">
                                    <XCircle className="h-3.5 w-3.5" /> {q.marks_awarded != null && Number(q.marks_awarded) !== 0 ? `${q.marks_awarded} pts (Incorrect)` : 'Incorrect'}
                                  </span>
                                )}
                                {isUnattempted && (
                                  <span className="inline-flex items-center text-xs font-medium text-muted-foreground bg-muted/60 px-2.5 py-1 rounded-full border border-border/50">
                                    Unanswered
                                  </span>
                                )}
                                <div className="p-1 rounded-md text-muted-foreground group-hover:text-primary group-hover:bg-muted/40 transition-colors">
                                  {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                </div>
                              </div>
                            </div>

                            {/* Clean Stats Row: Timing & Cohort Accuracy */}
                            <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                              <span className="flex items-center gap-1">
                                <Clock className="h-3.5 w-3.5 text-muted-foreground/70" />
                                <span>Time:</span>
                                <strong className={`font-semibold ${
                                  q.time_spent_seconds && q.target_time_seconds && q.time_spent_seconds > q.target_time_seconds * 1.5
                                    ? 'text-destructive'
                                    : 'text-foreground'
                                }`}>
                                  {q.time_spent_seconds != null && q.time_spent_seconds > 0 ? `${q.time_spent_seconds}s` : '0s'}
                                </strong>
                                {q.stats?.avg_time_spent_seconds != null && (
                                  <span className="text-muted-foreground/60">(Avg: {q.stats.avg_time_spent_seconds}s)</span>
                                )}
                              </span>

                              {q.target_time_seconds != null && (
                                <>
                                  <span className="text-muted-foreground/40">·</span>
                                  <span className="flex items-center gap-1">
                                    <Target className="h-3.5 w-3.5 text-muted-foreground/70" />
                                    <span>Target: <strong className="font-semibold text-foreground">{q.target_time_seconds}s</strong></span>
                                  </span>
                                </>
                              )}

                              {q.stats?.accuracy_percentage != null && (
                                <>
                                  <span className="text-muted-foreground/40">·</span>
                                  <span className="flex items-center gap-1">
                                    <Users className="h-3.5 w-3.5 text-muted-foreground/70" />
                                    <span>Accuracy: <strong className="font-semibold text-foreground">{q.stats.accuracy_percentage}%</strong></span>
                                  </span>
                                </>
                              )}
                            </div>

                            {/* Collapsed Snippet Preview */}
                            {!isExpanded && (
                              <div className="text-xs text-muted-foreground line-clamp-1 font-medium select-none pt-1">
                                <KatexRenderer text={q.question_text.slice(0, 180)} />
                              </div>
                            )}
                          </div>

                          {/* Expanded Content */}
                          {isExpanded && (
                            <div className="space-y-4 pt-2 border-t border-border/40 animate-fade-in">
                              {/* Question Context if present */}
                              {q.question_context && (
                                <QuestionContextCard
                                  context={q.question_context}
                                  format={q.render_format}
                                  className="mb-3"
                                />
                              )}

                              {/* Question Text (clean, no duplicate passage) */}
                              <div className="text-base font-semibold leading-relaxed text-foreground">
                                <KatexRenderer text={q.question_context ? (parseQuestionDirectionAndPrompt(q.question_text).prompt || q.question_text) : q.question_text} />
                              </div>

                              {/* Question Figure / Diagram */}
                              {q.figure_image_url && (
                                <div className="my-3 p-3 rounded-xl border border-border/70 bg-muted/20 flex flex-col items-center">
                                  <img
                                    src={q.figure_image_url}
                                    alt="Question figure"
                                    className="max-h-72 sm:max-h-80 w-auto max-w-full object-contain rounded-lg border border-border/50"
                                  />
                                </div>
                              )}

                              {/* Question Text Image */}
                              {q.question_image_url && (
                                <div className="my-3 p-3 rounded-xl border border-border/70 bg-muted/20 flex flex-col items-center">
                                  <img
                                    src={q.question_image_url}
                                    alt="Question image"
                                    className="max-h-72 sm:max-h-80 w-auto max-w-full object-contain rounded-lg border border-border/50"
                                  />
                                </div>
                              )}

                              {/* MCQ Options list */}
                              {q.options && q.options.length > 0 && (
                                <div className="space-y-2 pt-2">
                                  {q.options.map((opt) => {
                                    const isSelected = q.selected_option_ids && q.selected_option_ids.length > 0
                                      ? q.selected_option_ids.includes(opt.id)
                                      : opt.id === q.selected_option_id
                                    const isOptCorrect = opt.is_correct

                                    let containerStyle = 'border-border/60 bg-card hover:border-border'
                                    let labelStyle = 'bg-muted text-muted-foreground border border-border/60'
                                    let badgeContent = null

                                    if (isOptCorrect && isSelected) {
                                      containerStyle = 'border-secondary/40 bg-secondary/8 shadow-2xs'
                                      labelStyle = 'bg-secondary text-secondary-fg font-extrabold'
                                      badgeContent = (
                                        <span className="flex items-center gap-1 text-[11px] font-bold text-secondary bg-secondary/15 px-2.5 py-0.5 rounded-full border border-secondary/30 shrink-0">
                                          <CheckCircle2 className="h-3.5 w-3.5" /> Correct Answer & Selected
                                        </span>
                                      )
                                    } else if (isOptCorrect) {
                                      containerStyle = 'border-secondary/40 bg-secondary/6 shadow-2xs'
                                      labelStyle = 'bg-secondary text-secondary-fg font-extrabold'
                                      badgeContent = (
                                        <span className="flex items-center gap-1 text-[11px] font-bold text-secondary bg-secondary/15 px-2.5 py-0.5 rounded-full border border-secondary/30 shrink-0">
                                          <CheckCircle2 className="h-3.5 w-3.5" /> Correct Answer
                                        </span>
                                      )
                                    } else if (isSelected) {
                                      containerStyle = 'border-destructive/40 bg-destructive/8 shadow-2xs'
                                      labelStyle = 'bg-destructive text-destructive-foreground font-extrabold'
                                      badgeContent = (
                                        <span className="flex items-center gap-1 text-[11px] font-bold text-destructive bg-destructive/15 px-2.5 py-0.5 rounded-full border border-destructive/30 shrink-0">
                                          <XCircle className="h-3.5 w-3.5" /> Your Choice (Incorrect)
                                        </span>
                                      )
                                    }

                                    return (
                                      <div key={opt.id} className={`p-3 sm:p-3.5 rounded-xl border flex flex-col sm:flex-row sm:items-center justify-between gap-3 transition-all ${containerStyle}`}>
                                        <div className="flex items-start gap-3 min-w-0 flex-1">
                                          <span className={`flex h-6 w-6 sm:h-7 sm:w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${labelStyle}`}>
                                            {opt.label}
                                          </span>
                                          <div className="flex-1 pt-0.5 text-xs sm:text-sm font-medium leading-relaxed text-foreground overflow-x-auto space-y-2">
                                            {opt.text ? <KatexRenderer text={opt.text} /> : <span>Option {opt.label}</span>}
                                            {opt.option_image_url && (
                                              <img
                                                src={opt.option_image_url}
                                                alt={`Option ${opt.label}`}
                                                className="max-h-40 w-auto max-w-full object-contain rounded-lg border border-border/50 bg-background/50 p-1"
                                              />
                                            )}
                                          </div>
                                        </div>
                                        {badgeContent && (
                                          <div className="self-end sm:self-center shrink-0">
                                            {badgeContent}
                                          </div>
                                        )}
                                      </div>
                                    )
                                  })}
                                </div>
                              )}

                              {/* Numerical Answer Info */}
                              {q.question_type === 'NUMERICAL' && (
                                <div className="p-4 rounded-xl bg-muted/40 border border-border/40 text-xs space-y-1.5">
                                  <div className="flex items-center gap-2">
                                    <span className="text-muted-foreground font-semibold">Your Answer:</span>
                                    <strong className="text-foreground text-sm font-extrabold">{q.numerical_response ?? 'No response'}</strong>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-secondary font-semibold">Correct Answer:</span>
                                    <strong className="text-secondary text-sm font-extrabold">{q.numerical_answer}</strong>
                                  </div>
                                </div>
                              )}

                              {/* Solution / Explanation */}
                              {(q.structured_solution || q.explanation_text || q.explanation_image_url) && (
                                <div className="space-y-3 pt-1">
                                  <StructuredSolutionRenderer
                                    solution={q.structured_solution}
                                    fallbackExplanation={q.explanation_text}
                                    format={q.render_format}
                                  />
                                  {q.explanation_image_url && (
                                    <div className="p-3 rounded-xl border border-border/70 bg-muted/20 flex flex-col items-center">
                                      <img
                                        src={q.explanation_image_url}
                                        alt="Explanation diagram"
                                        className="max-h-72 w-auto max-w-full object-contain rounded-lg border border-border/50"
                                      />
                                      <span className="text-[11px] text-muted-foreground mt-1.5 font-medium">Solution Diagram</span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    )
                  })}

                  {/* Desktop Pagination Control Bar */}
                  <div className="flex flex-wrap items-center justify-between gap-4 p-4 rounded-2xl bg-card border border-border shadow-glass-sm">
                    <div className="flex items-center gap-2 text-xs font-bold text-muted-foreground">
                      <span>Show:</span>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 px-2.5 rounded-lg border-border text-xs font-bold text-foreground gap-1.5 hover:bg-muted/40"
                          >
                            <span>{pageSize} per page</span>
                            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-28 bg-card border-border shadow-glass-md rounded-xl p-1 z-50">
                          {[10, 20, 50].map((size) => (
                            <DropdownMenuItem
                              key={size}
                              onClick={() => {
                                setPageSize(size)
                                applyServerFilters({}, 1, size)
                              }}
                              className={`flex items-center justify-between px-2.5 py-1.5 text-xs font-semibold rounded-lg cursor-pointer transition-colors ${
                                pageSize === size
                                  ? 'bg-primary/10 text-primary font-bold'
                                  : 'text-foreground hover:bg-muted/40'
                              }`}
                            >
                              <span>{size} / page</span>
                              {pageSize === size && <Check className="h-3.5 w-3.5 text-primary" />}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <span>
                        Showing {Math.min((currentPage - 1) * pageSize + 1, totalFilteredCount)}–
                        {Math.min(currentPage * pageSize, totalFilteredCount)} of {totalFilteredCount}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={currentPage <= 1 || loadingQuestions}
                        onClick={() => applyServerFilters({}, Math.max(1, currentPage - 1), pageSize)}
                        className="gap-1 text-xs font-bold border-border"
                      >
                        <ChevronLeft className="h-4 w-4" /> Previous
                      </Button>

                      <div className="text-xs font-extrabold text-foreground px-2">
                        Page {currentPage} of {totalPages}
                      </div>

                      <Button
                        variant="outline"
                        size="sm"
                        disabled={currentPage >= totalPages || loadingQuestions}
                        onClick={() => applyServerFilters({}, Math.min(totalPages, currentPage + 1), pageSize)}
                        className="gap-1 text-xs font-bold border-primary/40 text-primary hover:bg-primary/10"
                      >
                        Next <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* TAB 4: ATTEMPT HISTORY */}
        {activeTab === 'history' && past_attempts.length > 1 && (
          <div className="space-y-4 animate-fade-in">
            <Card>
              <CardHeader className="pb-3 border-b border-border/40">
                <CardTitle className="text-base font-extrabold">Attempt Performance History</CardTitle>
              </CardHeader>
              <CardContent className="p-0 divide-y divide-border/40">
                {past_attempts.map((pa) => (
                  <div key={pa.id} className="p-4 flex items-center justify-between hover:bg-muted/30 transition-colors">
                    <div className="space-y-0.5">
                      <div className="font-extrabold text-sm text-foreground">Attempt #{pa.attempt_number}</div>
                      <div className="text-muted-foreground text-xs font-medium">{pa.completed_at}</div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="font-extrabold text-sm text-primary">
                          {formatNumber(pa.total_score)} / {formatNumber(pa.max_score)} pts
                        </div>
                        <div className="text-secondary text-xs font-bold">{formatNumber(pa.accuracy)}% Accuracy</div>
                      </div>
                      <Button size="sm" variant={pa.id === attempt.id ? 'default' : 'outline'} className="rounded-xl font-bold" asChild>
                        <Link href={`/student/attempts/${pa.id}/result`}>
                          {pa.id === attempt.id ? 'Current View' : 'View Report'}
                        </Link>
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      <ResumeAttemptModal
        open={conflictModalOpen}
        onOpenChange={setConflictModalOpen}
        ongoingAttempt={ongoing_attempt || null}
        onAbandonSuccess={() => initiateReattempt()}
      />
    </StudentShell>
  )
}
